import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    CpmButton,
    CpmModal,
    CpmModalBody,
    CpmModalFooter,
    CpmModalHeader,
    CpmStatusChip,
    CpmTypeChip,
} from '../../../components/design-system';
import {
    CPM_ALIAS_SCOPE,
    CPM_BARCODE_RESOLUTION,
    CPM_EXCISE_CATEGORY_LIST_LIMIT,
    CPM_LICENCE_EXCISE_COPY,
    CPM_MATCH_SIGNAL_ROWS,
    CPM_QUEUE_RESOLUTION,
    CPM_RECORD_TYPE,
    cpmLicenceExciseCreateHelper,
    cpmLicenceExciseInheritText,
    cpmLicenceExciseNeededHelper,
    formatCreatedVia,
    formatCpmCount,
    formatFlaggedDate,
    isActiveExciseCategory,
    linkReviewModelCopy,
    masterEditorRecordHref,
    packLabel,
    recordTypeLabel,
    sortExciseCategoriesForStoreLicence,
    sourceTypeToRecordType,
} from '../../../constants/centralProductMaster.constants';
import {
    approveQueueItem,
    dismissProductLink,
    getNextFlagged,
    listExciseCategories,
    listMasters,
    resolveBarcodeConflict,
    resolveProductLink,
} from '../../../services/productMasterService';
import type {
    CpmAliasScope,
    CpmBarcodeConflictItem,
    CpmBarcodeEntry,
    CpmBrandRecord,
    CpmExciseCategory,
    CpmLinkReviewItem,
    CpmMasterRecord,
    CpmMatchTraceSignals,
    CpmDuplicateCandidate,
    CpmPendingItem,
    CpmProductRecord,
    CpmVariantRecord,
} from '../../../types/centralProductMaster.types';
import LicensedExciseDropdown from '../components/LicensedExciseDropdown';
import { getApiErrorMessage } from '../../../utils/api/ApiClient';
import { toast } from '../../../utils/toast';

type ReviewMode = 'pending' | 'link' | 'barcode';
type LinkIntent = 'accept' | 'other' | 'new';
type CatalogSearchLevel = 'brand' | 'product' | 'variant';

type CatalogSearchHit = {
    _id: string;
    name: string;
    level: CatalogSearchLevel;
    meta: string;
};

function catalogSearchLevelFromPending(
    level: CpmPendingItem['level'] | undefined
): CatalogSearchLevel {
    if (level === 'brand' || level === 'product' || level === 'variant') return level;
    return 'product';
}

function catalogSearchPlaceholder(level: CatalogSearchLevel): string {
    if (level === 'brand') return 'Search brands by name';
    if (level === 'variant') return 'Search variants by name';
    return 'Search products by name';
}

function catalogSearchHitMeta(level: CatalogSearchLevel, record: CpmMasterRecord): string {
    if (level === 'brand') {
        const country = (record as CpmBrandRecord).countryOfOrigin?.trim();
        return country || 'Not recorded';
    }
    if (level === 'product') {
        const product = record as CpmProductRecord;
        const brand = product.brandInfo?.name?.trim() || '—';
        const abv =
            product.alcoholContent != null ? `${product.alcoholContent}% ABV` : 'Not recorded';
        return `${brand} · ${abv}`;
    }
    const variant = record as CpmVariantRecord;
    const brand = variant.brandInfo?.name?.trim() || variant.productInfo?.name?.trim() || '—';
    const volume =
        packLabel({ volumeMl: variant.volumeMl, volumeUnit: variant.volumeUnit }) || 'Not recorded';
    return `${brand} · ${volume}`;
}

function toCatalogSearchHit(level: CatalogSearchLevel, record: CpmMasterRecord): CatalogSearchHit {
    return {
        _id: String(record._id),
        name: String((record as { name?: string }).name ?? ''),
        level,
        meta: catalogSearchHitMeta(level, record),
    };
}

type SavePhase =
    | { status: 'idle' }
    | { status: 'busy'; label: string }
    | { status: 'failed'; label: string }
    | {
          status: 'done';
          label: string;
          hint: string;
          next?: { id: string; level: string } | null;
      };

interface ReviewDetailProps {
    open: boolean;
    mode: ReviewMode;
    pendingItem?: CpmPendingItem | null;
    linkItem?: CpmLinkReviewItem | null;
    barcodeItem?: CpmBarcodeConflictItem | null;
    onClose: () => void;
    onChanged: () => void;
    onOpenMerge?: (level: 'brand' | 'product' | 'variant', aId: string, bId: string) => void;
    onOpenReject?: (item: CpmPendingItem) => void;
    onOpenNext?: (id: string, level: string) => void;
}

function shortId(id: string): string {
    if (!id) return '—';
    return id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

function duplicateCandidateLabel(
    candidate: CpmDuplicateCandidate | null | undefined,
    fallbackId?: string | null
): string {
    const name = candidate?.name?.trim();
    if (name) return name;
    return shortId(fallbackId || candidate?.masterId || '');
}

function duplicateCandidateVolume(
    candidate: CpmDuplicateCandidate | null | undefined
): string | null {
    if (candidate == null) return null;
    return packLabel({
        volumeMl: candidate.volumeMl ?? undefined,
        volumeUnit: candidate.volumeUnit ?? undefined,
        containerType: candidate.containerType ?? undefined,
    });
}

function joinCountParts(parts: string[]): string {
    if (parts.length <= 1) return parts[0] || '';
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function childInfoText(level: string, children: { products: number; variants: number }): string {
    const parts: string[] = [];
    if (children.products > 0) {
        parts.push(`${children.products} ${children.products === 1 ? 'product' : 'products'}`);
    }
    if (children.variants > 0) {
        parts.push(`${children.variants} ${children.variants === 1 ? 'variant' : 'variants'}`);
    }
    if (parts.length === 0) return '';
    const total = (children.products || 0) + (children.variants || 0);
    const text = `${joinCountParts(parts)} under this ${level} ${
        total === 1 ? 'is' : 'are'
    } also pending`;
    return text.charAt(0).toUpperCase() + text.slice(1);
}

function claimantLabel(entry: CpmBarcodeEntry): string {
    return entry.name?.trim() || '';
}

function reportRows(entry: CpmBarcodeEntry) {
    return (entry.sources ?? []).map((s) => {
        const lastSoldAt = s.lastSoldAt ?? null;
        const hasSold = Boolean(lastSoldAt);
        return {
            tenant: s.storeBrandName?.trim() || s.storeBrand || 'Store brand',
            when: formatFlaggedDate(s.seenAt),
            soldLabel: hasSold ? `Last sold ${formatFlaggedDate(lastSoldAt)}` : 'Never sold',
            hasSold,
        };
    });
}

function droppedRows(item: CpmBarcodeConflictItem) {
    const rows: Array<{ tenant: string; when: string }> = [];
    for (const entry of item.entries ?? []) {
        for (const r of entry.removals ?? []) {
            rows.push({
                tenant: r.storeBrandName?.trim() || r.storeBrand || 'Store brand',
                when: formatFlaggedDate(r.removedAt),
            });
        }
    }
    return rows;
}

function fieldRow(label: string, value: string, warn = false) {
    return (
        <div
            className="flex items-center justify-between border-b border-[var(--cpm-border-default)] last:border-b-0"
            style={{ gap: 12, padding: '9px 0' }}
        >
            <span
                className="uppercase text-[var(--cpm-text-tertiary)]"
                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em' }}
            >
                {label}
            </span>
            <span
                className={
                    warn ? 'text-[var(--cpm-warning-text-med)]' : 'text-[var(--cpm-text-primary)]'
                }
                style={{ fontSize: 13, fontWeight: 800 }}
            >
                {value}
            </span>
        </div>
    );
}

function DuplicateStatusBadge({
    candidate,
}: {
    candidate: CpmDuplicateCandidate | null | undefined;
}) {
    if (candidate?.missing) {
        return (
            <span
                className="inline-flex items-center rounded-full bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)] uppercase"
                style={{
                    padding: '3px 9px',
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                }}
            >
                Record not found
            </span>
        );
    }
    if (candidate?.status) {
        return <CpmStatusChip status={candidate.status} />;
    }
    return null;
}

function duplicateCandidateFieldRows(candidate: CpmDuplicateCandidate | null | undefined) {
    const volume = duplicateCandidateVolume(candidate);
    return (
        <>
            {candidate?.missing ? fieldRow('Record', 'Not found', true) : null}
            {candidate?.brand?.name ? fieldRow('Brand', candidate.brand.name) : null}
            {candidate?.product?.name ? fieldRow('Product', candidate.product.name) : null}
            {volume ? fieldRow('Volume', volume) : null}
            {candidate?.status === 'MERGED'
                ? fieldRow('Merged into', candidate.mergedInto?.trim() || '—')
                : null}
        </>
    );
}

function metaChip(text: string, tone: 'plain' | 'warn' | 'mono' = 'plain') {
    const tones = {
        plain: 'bg-[var(--cpm-surface-card)] border-[var(--cpm-border-default)] text-[var(--cpm-text-secondary)]',
        warn: 'bg-[var(--cpm-warning-bg)] border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)]',
        mono: 'bg-[var(--cpm-surface-card)] border-[var(--cpm-border-default)] text-[var(--cpm-text-tertiary)] font-[family-name:var(--cpm-font-mono)]',
    };
    return (
        <span
            key={`${tone}-${text}`}
            className={`inline-flex items-center rounded-full border ${tones[tone]}`}
            style={{
                padding: '4px 10px',
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: tone === 'mono' ? '0.04em' : undefined,
            }}
        >
            {text}
        </span>
    );
}

export default function ReviewDetail({
    open,
    mode,
    pendingItem,
    linkItem,
    barcodeItem,
    onClose,
    onChanged,
    onOpenMerge,
    onOpenReject,
    onOpenNext,
}: ReviewDetailProps) {
    const [aliasScope, setAliasScope] = useState<CpmAliasScope>(CPM_ALIAS_SCOPE.STORE);
    const [savePhase, setSavePhase] = useState<SavePhase>({ status: 'idle' });
    const retryRef = useRef<null | (() => Promise<void>)>(null);
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState<CatalogSearchHit[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchReqIdRef = useRef(0);
    const [manualDup, setManualDup] = useState<{
        _id: string;
        name: string;
        meta: string;
    } | null>(null);
    const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
    const [linkIntent, setLinkIntent] = useState<LinkIntent>('accept');
    const [confirmReject, setConfirmReject] = useState(false);
    const [dupExpanded, setDupExpanded] = useState(false);
    const [winningVariantId, setWinningVariantId] = useState<string | null>(null);
    const [applyToTenants, setApplyToTenants] = useState(false);
    const [dryRunPreview, setDryRunPreview] = useState<{
        wouldAddCount: number;
        wouldRemoveCount: number;
        affectedTenantDocumentCount: number;
    } | null>(null);
    const [exciseCategoryId, setExciseCategoryId] = useState<string | null>(null);
    const [exciseCategories, setExciseCategories] = useState<CpmExciseCategory[]>([]);

    const busy = savePhase.status === 'busy';
    const searchLevel = catalogSearchLevelFromPending(pendingItem?.level);

    useEffect(() => {
        if (!open) return;
        setSavePhase({ status: 'idle' });
        retryRef.current = null;
        setConfirmReject(false);
        setDryRunPreview(null);
        setSearch('');
        setSearchResults([]);
        setSearchLoading(false);
        setManualDup(null);
        setExciseCategoryId(null);
        setExciseCategories([]);
        searchReqIdRef.current += 1;
    }, [open, mode, pendingItem?._id, linkItem?._id, barcodeItem?.barcode]);

    useEffect(() => {
        if (!open || mode !== 'pending') return;

        const query = search.trim();
        if (query.length < 2) {
            setSearchResults([]);
            setSearchLoading(false);
            searchReqIdRef.current += 1;
            return;
        }

        setSearchLoading(true);
        const reqId = ++searchReqIdRef.current;
        const timer = window.setTimeout(async () => {
            try {
                const data = await listMasters({
                    level: searchLevel,
                    search: query,
                    page: 1,
                    limit: 20,
                });
                if (reqId !== searchReqIdRef.current) return;
                setSearchResults(
                    (data.items ?? []).map((record) => toCatalogSearchHit(searchLevel, record))
                );
            } catch (err) {
                if (reqId !== searchReqIdRef.current) return;
                setSearchResults([]);
                toast.error(getApiErrorMessage(err));
            } finally {
                if (reqId === searchReqIdRef.current) setSearchLoading(false);
            }
        }, 300);

        return () => {
            window.clearTimeout(timer);
        };
    }, [open, mode, search, searchLevel]);

    const linkIsVariant =
        mode === 'link' &&
        sourceTypeToRecordType(linkItem?.sourceType ?? '') === CPM_RECORD_TYPE.VARIANT;
    const tenantState = String(
        linkItem?.tenantState ?? (linkItem as { state?: string } | null)?.state ?? ''
    ).trim();
    const licensedExciseCategoryIds = Array.isArray(linkItem?.licensedExciseCategoryIds)
        ? linkItem.licensedExciseCategoryIds.map(String)
        : [];

    useEffect(() => {
        if (!open || mode !== 'link' || !linkIsVariant || !tenantState) return;
        let cancelled = false;
        void listExciseCategories({
            state: tenantState,
            page: 1,
            limit: CPM_EXCISE_CATEGORY_LIST_LIMIT,
        })
            .then((data) => {
                if (cancelled) return;
                setExciseCategories(
                    (data.categories ?? []).filter((c) => isActiveExciseCategory(c))
                );
            })
            .catch((err) => {
                if (cancelled) return;
                setExciseCategories([]);
                toast.error(getApiErrorMessage(err));
            });
        return () => {
            cancelled = true;
        };
    }, [open, mode, linkIsVariant, tenantState, linkItem?._id]);

    const commit = async (
        label: string,
        doneHint: string,
        run: () => Promise<{ next?: { id: string; level: string } | null } | void>
    ) => {
        const execute = async () => {
            setSavePhase({ status: 'busy', label });
            try {
                const result = await run();
                onChanged();
                const next =
                    result && typeof result === 'object' && 'next' in result
                        ? (result.next ?? null)
                        : null;
                setSavePhase({
                    status: 'done',
                    label,
                    hint: doneHint,
                    next,
                });
            } catch {
                setSavePhase({ status: 'failed', label });
            }
        };
        retryRef.current = execute;
        await execute();
    };

    const title = useMemo(() => {
        if (mode === 'pending') return pendingItem?.name ?? 'Pending record';
        if (mode === 'link') {
            return (
                linkItem?.name?.trim() ||
                linkItem?.sourceId ||
                `Link review · ${recordTypeLabel(sourceTypeToRecordType(linkItem?.sourceType ?? ''))}`
            );
        }
        return barcodeItem?.barcode ?? 'Barcode conflict';
    }, [mode, pendingItem, linkItem, barcodeItem]);

    const parentBlocked = !!(
        mode === 'pending' &&
        pendingItem &&
        !pendingItem.parentIsActive &&
        pendingItem.parent
    );
    const showChildren =
        mode === 'pending' &&
        !!pendingItem?.children &&
        ((pendingItem.children.products ?? 0) > 0 || (pendingItem.children.variants ?? 0) > 0);
    const childrenBannerText =
        showChildren && pendingItem?.children
            ? childInfoText(pendingItem.level, pendingItem.children)
            : '';
    const isLive = mode === 'pending' && pendingItem?.status === 'ACTIVE';
    const hasDup = !!pendingItem?.possibleDuplicateOf;
    const topDup =
        pendingItem?.possibleDuplicate ??
        pendingItem?.possibleDuplicateCandidates?.find(
            (c) => c.masterId === pendingItem?.possibleDuplicateOf
        ) ??
        pendingItem?.possibleDuplicateCandidates?.[0] ??
        null;
    const dupAlts = (pendingItem?.possibleDuplicateCandidates ?? []).filter(
        (c) => c.masterId !== pendingItem?.possibleDuplicateOf
    );
    const mergeTargetId = manualDup?._id ?? pendingItem?.possibleDuplicateOf ?? null;

    const linkCandidates = linkItem?.matchTrace?.candidates ?? [];
    const aiPick = linkCandidates[0];
    const chosenId =
        linkIntent === 'new' ? 'NEW' : selectedMasterId || aiPick?.productVariantId || null;
    const chosenCand = linkCandidates.find((c) => c.productVariantId === chosenId) ?? aiPick;
    const correcting =
        mode === 'link' &&
        (linkIntent === 'new' || (!!chosenId && !!aiPick && chosenId !== aiPick.productVariantId));
    const topHasLiveStateProduct = linkItem?.hasLiveStateProductForTopCandidate === true;
    const chosenIsTopCandidate =
        linkIntent !== 'new' &&
        chosenId != null &&
        aiPick?.productVariantId != null &&
        chosenId === aiPick.productVariantId;
    const stateProductExists = chosenIsTopCandidate && topHasLiveStateProduct;
    const willCreateStateProduct = linkIntent === 'new' ? true : !stateProductExists;
    const exciseRequired = linkIsVariant && willCreateStateProduct;
    const showExcisePicker =
        mode === 'link' &&
        linkIsVariant &&
        !confirmReject &&
        savePhase.status !== 'busy' &&
        savePhase.status !== 'done';
    const exciseBlocked =
        exciseRequired && !exciseCategoryId && !confirmReject && savePhase.status === 'idle';
    const orderedExciseOptions = useMemo(() => {
        return sortExciseCategoriesForStoreLicence(exciseCategories, licensedExciseCategoryIds).map(
            (c) => ({
                id: String(c._id),
                label: c.category,
                licensed: c.licensed,
            })
        );
    }, [exciseCategories, licensedExciseCategoryIds]);
    const pickedExcise = orderedExciseOptions.find((o) => o.id === exciseCategoryId) ?? null;
    const licSentence = licensedExciseCategoryIds.length
        ? CPM_LICENCE_EXCISE_COPY.LICENSED_FIRST
        : CPM_LICENCE_EXCISE_COPY.NO_LICENCES;
    const exciseStateLabel = tenantState || 'this state';
    const exciseHelper =
        linkIntent === 'new'
            ? `${cpmLicenceExciseCreateHelper(exciseStateLabel)} ${licSentence}`
            : `${cpmLicenceExciseNeededHelper(exciseStateLabel)}${
                  stateProductExists ? '' : ` ${CPM_LICENCE_EXCISE_COPY.THIS_ONE_DOES_NOT}`
              } ${licSentence}`;
    const nearDupPeerName = (() => {
        if (!linkItem?.matchTrace?.nearDuplicateClusterDetected) return '';
        const flagged = linkCandidates.find(
            (c) => c.nearDuplicateClusterFlag && c.productVariantId !== aiPick?.productVariantId
        );
        if (flagged?.name?.trim()) return flagged.name.trim();
        return linkCandidates[1]?.name?.trim() || '';
    })();
    const linkModelCopy = linkReviewModelCopy({
        method: linkItem?.method,
        model: linkItem?.matchTrace?.model,
        reviewedByName: linkItem?.reviewedByName,
    });
    const aiPickSignals = (aiPick?.signals ?? {}) as CpmMatchTraceSignals;
    const aiPickScore =
        aiPick?.compositeConfidence != null
            ? Number(aiPick.compositeConfidence)
            : linkItem?.confidence != null
              ? Number(linkItem.confidence)
              : null;

    const isBarcodeConflicted = !!(
        barcodeItem &&
        (barcodeItem.claimCount > 1 || (barcodeItem.entries?.length ?? 0) > 1)
    );
    const barcodeDropped = barcodeItem ? droppedRows(barcodeItem) : [];
    const barcodeClaimants = barcodeItem?.entries ?? [];
    const rankedClaimants = useMemo(() => {
        return [...barcodeClaimants].sort(
            (a, b) => (b.sources?.length ?? 0) - (a.sources?.length ?? 0)
        );
    }, [barcodeClaimants]);
    const topReportN = rankedClaimants[0]?.sources?.length ?? 0;
    const reporterCount = useMemo(() => {
        const seen = new Set<string>();
        for (const e of barcodeClaimants) {
            for (const s of e.sources ?? []) {
                seen.add(String(s.storeBrandName || s.storeBrand || s.storeBrandVariant || ''));
            }
        }
        return seen.size;
    }, [barcodeClaimants]);

    const originLabel =
        mode === 'pending'
            ? 'Pending Promotion'
            : mode === 'link'
              ? 'Link Review'
              : isBarcodeConflicted
                ? 'Barcode Conflict'
                : 'Disputed Barcode';

    const clearCatalogSearch = () => {
        searchReqIdRef.current += 1;
        setSearch('');
        setSearchResults([]);
        setSearchLoading(false);
    };

    const handleApprove = async () => {
        if (!pendingItem || parentBlocked) return;
        await commit(
            'Promoted to Active',
            'The record is now trusted and available to every tenant.',
            async () => {
                await approveQueueItem({ level: pendingItem.level, masterId: pendingItem._id });
                try {
                    const next = await getNextFlagged({ afterId: pendingItem._id });
                    return { next: next.next ?? null };
                } catch {
                    return { next: null };
                }
            }
        );
    };

    const handleResolve = async (resolution: string) => {
        if (!linkItem) return;
        const chosenName =
            resolution === CPM_QUEUE_RESOLUTION.CREATE_NEW
                ? 'New master record created'
                : `Linked to ${chosenCand?.name ?? 'selected master'}`;
        const doneHint =
            resolution === CPM_QUEUE_RESOLUTION.CREATE_NEW
                ? 'No candidate was linked. The tenant record became its own master.'
                : 'The tenant record now reports under the chosen master.';
        const catNote = pickedExcise
            ? ` The state product for ${exciseStateLabel} is created under ${pickedExcise.label}.`
            : '';
        await commit(chosenName, doneHint + catNote, async () => {
            await resolveProductLink({
                productLinkId: linkItem._id,
                resolution: resolution as typeof CPM_QUEUE_RESOLUTION.ACCEPT_AI,
                selectedMasterId:
                    resolution === CPM_QUEUE_RESOLUTION.PICK_DIFFERENT
                        ? (selectedMasterId ?? undefined)
                        : undefined,
                aliasScope,
                ...(exciseCategoryId ? { exciseCategoryId } : {}),
            });
        });
    };

    const handleDismiss = async () => {
        if (!linkItem) return;
        await commit(
            'Rejected and left unlinked',
            'This item is out of the queue. Nothing was linked and no master record was created.',
            async () => {
                await dismissProductLink({ productLinkId: linkItem._id });
            }
        );
    };

    const handleBarcode = async (resolution: string, dryRun?: boolean) => {
        const entry =
            barcodeItem?.entries?.find(
                (e) => String(e.productVariant) === String(winningVariantId)
            ) ?? barcodeItem?.entries?.[0];
        if (!entry) return;

        if (dryRun) {
            try {
                setSavePhase({ status: 'busy', label: 'Dry-run preview' });
                const result = await resolveBarcodeConflict({
                    barcodeRegistryId: entry._id,
                    resolution: resolution as typeof CPM_BARCODE_RESOLUTION.ASSIGN_TO,
                    winningProductVariantId:
                        resolution === CPM_BARCODE_RESOLUTION.ASSIGN_TO
                            ? (winningVariantId ?? undefined)
                            : undefined,
                    applyToTenants:
                        resolution === CPM_BARCODE_RESOLUTION.ACKNOWLEDGE
                            ? undefined
                            : applyToTenants,
                    dryRun: true,
                });
                if (result && 'wouldAddCount' in result) {
                    setDryRunPreview(result as typeof dryRunPreview);
                }
                setSavePhase({ status: 'idle' });
            } catch (err) {
                setSavePhase({ status: 'idle' });
                toast.error(getApiErrorMessage(err));
            }
            return;
        }

        const winnerName = claimantLabel(entry);
        let label = 'Barcode conflict resolved';
        let hint = 'The barcode registry was updated.';
        if (resolution === CPM_BARCODE_RESOLUTION.ASSIGN_TO) {
            label = `Barcode assigned to ${winnerName}`;
            hint = applyToTenants
                ? `The registry and affected tenant gtin lists now point at ${winnerName}.`
                : 'The registry drops the barcode from the other claimants. Their store brands keep it until they re-scan.';
        } else if (resolution === CPM_BARCODE_RESOLUTION.ACKNOWLEDGE) {
            label = 'Left trusted';
            hint = `The barcode still auto-links to ${winnerName}. The drop-off is noted and the signal clears.`;
        } else if (resolution === CPM_BARCODE_RESOLUTION.RETIRE) {
            label = 'Barcode retired';
            hint = 'The catalog stops auto-linking this barcode.';
        }

        await commit(label, hint, async () => {
            await resolveBarcodeConflict({
                barcodeRegistryId: entry._id,
                resolution: resolution as typeof CPM_BARCODE_RESOLUTION.ASSIGN_TO,
                winningProductVariantId:
                    resolution === CPM_BARCODE_RESOLUTION.ASSIGN_TO
                        ? (winningVariantId ?? undefined)
                        : undefined,
                applyToTenants:
                    resolution === CPM_BARCODE_RESOLUTION.ACKNOWLEDGE ? undefined : applyToTenants,
                dryRun: undefined,
            });
        });
    };

    const openMerge = (otherId: string) => {
        if (!pendingItem || !onOpenMerge) return;
        onOpenMerge(pendingItem.level, pendingItem._id, otherId);
    };

    const pickCandidate = (id: string, isAi: boolean) => {
        setSelectedMasterId(id);
        setLinkIntent(isAi ? 'accept' : 'other');
        setConfirmReject(false);
    };

    const toggleNewProduct = () => {
        if (linkIntent === 'new') {
            setLinkIntent('accept');
            setSelectedMasterId(null);
        } else {
            setLinkIntent('new');
            setSelectedMasterId('NEW');
        }
        setConfirmReject(false);
    };

    const pendingVerdict = (() => {
        if (!pendingItem) return { label: '', hint: '', danger: false };
        if (isLive) {
            return {
                label: `This ${pendingItem.level} is already live`,
                hint: 'It was approved earlier and is not part of this pending batch — there is nothing to approve or reject here.',
                danger: false,
            };
        }
        if (parentBlocked) {
            return {
                label: 'Approve is blocked',
                hint: `This ${pendingItem.level} can’t be approved until its parent ${pendingItem.parent?.level?.toLowerCase()} “${pendingItem.parent?.name}” is approved. Open the parent first — you can still reject or merge this record.`,
                danger: true,
            };
        }
        if (hasDup) {
            return {
                label: 'One record, or two?',
                hint: 'Approving promotes it to Active as-is. The merge option opens a separate merge decision — nothing is merged yet.',
                danger: false,
            };
        }
        if (manualDup) {
            return {
                label: 'You found a possible duplicate',
                hint: `Approving still promotes as-is. Use “go to merge” to open a merge decision against ${manualDup.name}.`,
                danger: false,
            };
        }
        return {
            label: 'Nothing flagged on this record',
            hint: 'Approving promotes it from Pending to Active — or search the catalog below if you suspect a duplicate.',
            danger: false,
        };
    })();

    const linkVerdict = (() => {
        if (confirmReject) {
            return {
                label: 'Reject this item and leave it unlinked?',
                hint: 'It moves out of the queue as rejected — nothing is linked and no new record is created. This can\u2019t be undone from here.',
                danger: true,
            };
        }
        if (linkIntent === 'new') {
            return {
                label: 'Creating a new master record',
                hint: 'No candidate will be linked. The tenant record becomes its own master.',
                danger: false,
            };
        }
        if (correcting && chosenCand) {
            return {
                label: 'Correcting the AI',
                hint: `You picked ${chosenCand.name} instead of the suggested match.`,
                danger: false,
            };
        }
        return {
            label: 'Accepting the AI\u2019s pick',
            hint: aiPick
                ? `Links this tenant record to ${aiPick.name}.`
                : 'No AI candidate available.',
            danger: false,
        };
    })();

    const winnerEntry = barcodeClaimants.find(
        (c) => String(c.productVariant) === String(winningVariantId)
    );
    const barcodeVerdict = (() => {
        if (!barcodeItem) return { label: '', hint: '', danger: false };
        if (!isBarcodeConflicted) {
            return {
                label: `${formatCpmCount(barcodeDropped.length)} store brands stopped using a trusted barcode`,
                hint: 'Nothing is broken — decide whether to keep trusting it or retire it before the next import.',
                danger: false,
            };
        }
        if (winnerEntry) {
            return {
                label: `Assigning to ${claimantLabel(winnerEntry)}`,
                hint: applyToTenants
                    ? 'Registry updated plus tenant gtin lists for affected store brands.'
                    : 'The registry drops the barcode from the other claimants. Their store brands keep it until they re-scan.',
                danger: false,
            };
        }
        return {
            label: 'No winner chosen yet',
            hint: `Pick one of the ${formatCpmCount(barcodeClaimants.length)} claimants, or leave it ambiguous until more stores report.`,
            danger: false,
        };
    })();

    const evidenceCards = (() => {
        if (!barcodeItem)
            return [] as Array<{ tone: 'success' | 'warn' | 'drop'; title: string; body: string }>;
        const cards: Array<{ tone: 'success' | 'warn' | 'drop'; title: string; body: string }> = [];
        if (isBarcodeConflicted) {
            const leaders = rankedClaimants.filter((c) => (c.sources?.length ?? 0) === topReportN);
            for (const c of rankedClaimants) {
                const reps = reportRows(c);
                const names = reps.map((r) => r.tenant);
                const dates = reps.map((r) => r.when);
                const isLeader = reps.length === topReportN && leaders.length === 1;
                cards.push({
                    tone: isLeader ? 'success' : 'warn',
                    title:
                        reps.length === 1
                            ? `One store brand claims ${claimantLabel(c)}`
                            : `${reps.length} store brands back ${claimantLabel(c)}`,
                    body:
                        reps.length <= 1
                            ? `A single report from ${names[0] ?? 'a store'}, first reported ${dates[0] ?? '—'}, with nothing corroborating it.`
                            : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} each reported this barcode against this product (first reported ${dates.join(', ')}).`,
                });
            }
        }
        if (barcodeDropped.length) {
            cards.push({
                tone: 'drop',
                title: `${barcodeDropped.length} store brands have stopped using this barcode`,
                body: `${barcodeDropped.map((d) => `${d.tenant} (stopped using ${d.when})`).join(', ')}. The catalog still trusts this barcode and keeps auto-linking it.`,
            });
        }
        return cards;
    })();

    const evidenceLead = !barcodeItem
        ? ''
        : !isBarcodeConflicted
          ? `This barcode is trusted and still auto-links to ${claimantLabel(barcodeClaimants[0] ?? ({} as CpmBarcodeEntry))}. Nothing is broken — but ${barcodeDropped.length} store brands that used to report it have stopped, which is worth a look.`
          : rankedClaimants.filter((c) => (c.sources?.length ?? 0) === topReportN).length === 1
            ? `A barcode belongs to one product. ${claimantLabel(rankedClaimants[0])} has the most independent store brands behind it — ${topReportN} of ${reporterCount} reporting — which is usually the correct owner.`
            : `A barcode belongs to one product. ${barcodeClaimants.length} products are claiming this one, and no claimant is better corroborated than the rest yet.`;

    const outcomeVerdict = (() => {
        if (savePhase.status === 'busy') {
            return {
                label: `Saving — ${savePhase.label.toLowerCase()}…`,
                hint: 'Writing the decision to the catalog. Do not close this window.',
                danger: false,
            };
        }
        if (savePhase.status === 'failed') {
            return {
                label: 'That did not save',
                hint: `The catalog service did not confirm “${savePhase.label}”. Nothing was changed — try again.`,
                danger: true,
            };
        }
        if (savePhase.status === 'done') {
            return {
                label: savePhase.label,
                hint: savePhase.hint,
                danger: false,
            };
        }
        return null;
    })();

    const outcomeActions =
        savePhase.status === 'busy' ? (
            <span className="cpm-spin" style={{ width: 18, height: 18 }} />
        ) : savePhase.status === 'failed' ? (
            <>
                <CpmButton
                    variant="navy"
                    size="lg"
                    onClick={() => void retryRef.current?.()}
                    leadingIcon={
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                            <path d="M3 3v5h5" />
                        </svg>
                    }
                >
                    Try again
                </CpmButton>
                <CpmButton variant="outline" size="lg" onClick={onClose}>
                    Back to the queue
                </CpmButton>
            </>
        ) : savePhase.status === 'done' ? (
            <>
                {savePhase.next && onOpenNext ? (
                    <CpmButton
                        variant="navy"
                        size="lg"
                        onClick={() => {
                            if (!savePhase.next) return;
                            onOpenNext(savePhase.next.id, savePhase.next.level);
                            setSavePhase({ status: 'idle' });
                        }}
                    >
                        Next item →
                    </CpmButton>
                ) : null}
                <CpmButton
                    variant={savePhase.next && onOpenNext ? 'outline' : 'navy'}
                    size="lg"
                    onClick={onClose}
                >
                    Back to the queue
                </CpmButton>
            </>
        ) : null;

    const renderVerdictRow = (
        verdict: { label: string; hint: string; danger: boolean },
        actions: ReactNode
    ) => {
        const shown = outcomeVerdict ?? verdict;
        const shownActions = outcomeActions ?? actions;
        return (
            <div className="flex flex-wrap items-center" style={{ gap: 14 }}>
                <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
                    <span
                        className={
                            shown.danger
                                ? 'text-[var(--cpm-shortfall-text)]'
                                : 'text-[var(--cpm-brand-navy)]'
                        }
                        style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '-0.01em' }}
                    >
                        {shown.label}
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
                    >
                        {shown.hint}
                    </span>
                </div>
                <div className="ml-auto flex flex-wrap items-center" style={{ gap: 14 }}>
                    {exciseBlocked ? (
                        <span
                            className="inline-flex items-center text-[var(--cpm-warning-text)]"
                            style={{ gap: 7, fontSize: 12, fontWeight: 800 }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                className="shrink-0"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 16v-4" strokeLinecap="round" />
                                <path d="M12 8h.01" strokeLinecap="round" />
                            </svg>
                            {CPM_LICENCE_EXCISE_COPY.PICK_TO_CREATE}
                        </span>
                    ) : null}
                    <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
                        {shownActions}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <CpmModal open={open} onClose={onClose} maxWidth={1340} zIndex={70} variant="page">
            <CpmModalHeader className="!px-7 !pt-4 !pb-[18px]">
                <div className="flex items-center" style={{ gap: 14, marginBottom: 14 }}>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center text-[var(--cpm-text-secondary)] hover:text-[var(--cpm-brand-navy)]"
                        style={{ gap: 7, fontSize: 12.5, fontWeight: 800 }}
                    >
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                        >
                            <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Back to the queue
                    </button>
                    <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                        /
                    </span>
                    <span
                        className={[
                            'inline-flex items-center rounded-full uppercase',
                            mode === 'pending'
                                ? 'bg-[var(--cpm-info-bg)] text-[var(--cpm-info-text)] border border-[var(--cpm-info-border)]'
                                : mode === 'link'
                                  ? 'bg-[var(--cpm-warning-bg)] text-[var(--cpm-warning-text)] border border-[var(--cpm-warning-border)]'
                                  : isBarcodeConflicted
                                    ? 'bg-[var(--cpm-shortfall-bg)] text-[var(--cpm-shortfall-text)] border border-[var(--cpm-shortfall-border)]'
                                    : 'bg-[var(--cpm-warning-bg)] text-[var(--cpm-warning-text)] border border-[var(--cpm-warning-border)]',
                        ].join(' ')}
                        style={{
                            padding: '3px 10px',
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: '0.05em',
                        }}
                    >
                        {originLabel}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        title="Close"
                        className="ml-auto inline-flex items-center justify-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-text-secondary)]"
                        style={{ width: 34, height: 34, borderRadius: 10 }}
                    >
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
                <div className="flex items-end justify-between flex-wrap" style={{ gap: 28 }}>
                    <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
                        <h1
                            className={[
                                'm-0 !font-extrabold !tracking-[-0.025em] !leading-[1.15] !text-[var(--cpm-brand-navy)]',
                                mode === 'barcode'
                                    ? '!text-[30px] font-[family-name:var(--cpm-font-mono)]'
                                    : '!text-[26px]',
                            ].join(' ')}
                        >
                            {title}
                        </h1>
                        <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                            {mode === 'pending' && pendingItem ? (
                                <>
                                    <CpmTypeChip recordType={pendingItem.recordType} />
                                    {pendingItem.level === 'variant' && packLabel(pendingItem)
                                        ? metaChip(
                                              `${packLabel(pendingItem)} · ${pendingItem.containerType || 'Container not recorded'}`
                                          )
                                        : null}
                                    {isLive
                                        ? metaChip('Already live')
                                        : metaChip(
                                              `Created ${formatFlaggedDate(pendingItem.createdAt)}`
                                          )}
                                    {hasDup
                                        ? metaChip('Possible duplicate', 'warn')
                                        : !isLive
                                          ? metaChip('No duplicate flag')
                                          : null}
                                </>
                            ) : null}
                            {mode === 'link' && linkItem ? (
                                <>
                                    <CpmTypeChip
                                        recordType={sourceTypeToRecordType(linkItem.sourceType)}
                                    />
                                    {linkItem.storeBrandName || linkItem.storeBrand
                                        ? metaChip(linkItem.storeBrandName || linkItem.storeBrand)
                                        : null}
                                    {metaChip(
                                        `${linkItem.confidenceBand} · ${linkItem.method}`,
                                        'mono'
                                    )}
                                    {metaChip(`Flagged ${formatFlaggedDate(linkItem.createdAt)}`)}
                                </>
                            ) : null}
                            {mode === 'barcode' && barcodeItem ? (
                                <>
                                    {metaChip('Barcode / GTIN')}
                                    {isBarcodeConflicted
                                        ? metaChip(
                                              `${formatCpmCount(barcodeClaimants.length)} claiming products`,
                                              'warn'
                                          )
                                        : metaChip('Trusted — auto-linking')}
                                    {metaChip(
                                        `${formatCpmCount(reporterCount)} store brand${reporterCount === 1 ? '' : 's'} reporting`
                                    )}
                                    {barcodeDropped.length
                                        ? metaChip(
                                              `${formatCpmCount(barcodeDropped.length)} stopped reporting`,
                                              'warn'
                                          )
                                        : null}
                                    {metaChip(
                                        `${isBarcodeConflicted ? 'First conflict' : 'First drop-off'} ${formatFlaggedDate(barcodeItem.firstConflictAt)}`
                                    )}
                                </>
                            ) : null}
                        </div>
                    </div>
                    {mode === 'pending' && pendingItem ? (
                        <div className="flex flex-col items-end" style={{ gap: 4 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                            >
                                Status
                            </span>
                            <span
                                className={
                                    isLive
                                        ? 'text-[var(--cpm-success-text-light)]'
                                        : 'text-[var(--cpm-warning-text)]'
                                }
                                style={{
                                    fontSize: 28,
                                    fontWeight: 800,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1,
                                }}
                            >
                                {pendingItem.status}
                            </span>
                        </div>
                    ) : null}
                    {mode === 'link' && linkItem ? (
                        <div className="flex flex-col items-end" style={{ gap: 4 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                            >
                                AI match score
                            </span>
                            <span
                                className="text-[var(--cpm-warning-text)]"
                                style={{
                                    fontSize: 28,
                                    fontWeight: 800,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1,
                                }}
                            >
                                {Math.round((linkItem.confidence ?? 0) * 100)}%
                            </span>
                        </div>
                    ) : null}
                    {mode === 'barcode' && barcodeItem ? (
                        <div className="flex flex-col items-end" style={{ gap: 4 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                            >
                                {isBarcodeConflicted ? 'Claimants' : 'Stopped using it'}
                            </span>
                            <span
                                className={
                                    isBarcodeConflicted
                                        ? 'text-[var(--cpm-shortfall-text)]'
                                        : 'text-[var(--cpm-warning-text)]'
                                }
                                style={{
                                    fontSize: 28,
                                    fontWeight: 800,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1,
                                }}
                            >
                                {isBarcodeConflicted
                                    ? barcodeClaimants.length
                                    : barcodeDropped.length}
                            </span>
                        </div>
                    ) : null}
                </div>
            </CpmModalHeader>

            <CpmModalBody clampScroll={false}>
                {/* ─── PENDING ─── */}
                {mode === 'pending' && pendingItem ? (
                    <>
                        {parentBlocked ? (
                            <div
                                className="flex items-start bg-[var(--cpm-shortfall-bg)] border-[1.5px] border-[var(--cpm-shortfall-border)]"
                                style={{ gap: 13, padding: '15px 18px', borderRadius: 14 }}
                            >
                                <span
                                    className="shrink-0 inline-flex items-center justify-center bg-white border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                                    style={{ width: 34, height: 34, borderRadius: 9 }}
                                >
                                    <svg
                                        width="17"
                                        height="17"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <rect width="18" height="11" x="3" y="11" rx="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                </span>
                                <div className="flex flex-col min-w-0" style={{ gap: 8 }}>
                                    <div className="flex flex-col" style={{ gap: 3 }}>
                                        <span
                                            className="text-[var(--cpm-shortfall-text)]"
                                            style={{
                                                fontSize: 13.5,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            Approval blocked by unapproved parent
                                        </span>
                                        <span
                                            className="text-[var(--cpm-shortfall-text-med)]"
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            This {pendingItem.level} can&apos;t be approved yet —
                                            its parent {pendingItem.parent?.level?.toLowerCase()} “
                                            {pendingItem.parent?.name}” is still awaiting approval.
                                            Approve the parent first, then come back to this record.
                                        </span>
                                    </div>
                                    {pendingItem.parent?.id ? (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                onOpenNext?.(
                                                    pendingItem.parent!.id,
                                                    pendingItem.parent!.level
                                                )
                                            }
                                            className="self-start inline-flex items-center bg-white border-[1.5px] border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                                            style={{
                                                gap: 7,
                                                height: 34,
                                                paddingInline: 14,
                                                borderRadius: 9,
                                                fontSize: 12.5,
                                                fontWeight: 800,
                                            }}
                                        >
                                            Go to {pendingItem.parent.level?.toLowerCase()} “
                                            {pendingItem.parent.name}” →
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        {showChildren && childrenBannerText ? (
                            <div
                                className="flex items-center flex-wrap bg-[var(--cpm-info-bg)] border border-[var(--cpm-info-border)]"
                                style={{ gap: 13, padding: '14px 18px', borderRadius: 14 }}
                            >
                                <span
                                    className="shrink-0 inline-flex items-center justify-center bg-white border border-[var(--cpm-info-border)] text-[var(--cpm-info-text)]"
                                    style={{ width: 32, height: 32, borderRadius: 9 }}
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 3v16a2 2 0 0 0 2 2h16" />
                                        <path d="M7 16h8" />
                                        <path d="M7 11h12" />
                                        <path d="M7 6h3" />
                                    </svg>
                                </span>
                                <div
                                    className="flex flex-col min-w-[200px] flex-1"
                                    style={{ gap: 2 }}
                                >
                                    <span
                                        className="text-[var(--cpm-info-text)]"
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 800,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {childrenBannerText}
                                    </span>
                                    <span
                                        className="text-[var(--cpm-info-text)]"
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            lineHeight: 1.45,
                                            opacity: 0.85,
                                        }}
                                    >
                                        This is informational — you can approve this record without
                                        approving its children first.
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="shrink-0 inline-flex items-center bg-white border-[1.5px] border-[var(--cpm-info-border)] text-[var(--cpm-info-text)]"
                                    style={{
                                        gap: 7,
                                        height: 34,
                                        paddingInline: 14,
                                        borderRadius: 9,
                                        fontSize: 12.5,
                                        fontWeight: 800,
                                    }}
                                >
                                    View the pending records beneath this →
                                </button>
                            </div>
                        ) : null}

                        <div className="flex flex-col" style={{ gap: 12 }}>
                            <div className="flex items-baseline" style={{ gap: 12 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    Comparison
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 13, fontWeight: 600 }}
                                >
                                    {hasDup
                                        ? 'Is this the same product as the live catalog record?'
                                        : 'Confirm this record is a real product before promoting it.'}
                                </span>
                            </div>
                            <div
                                className="grid"
                                style={{
                                    gridTemplateColumns: hasDup
                                        ? 'repeat(auto-fit, minmax(340px, 1fr))'
                                        : 'minmax(0, 1fr)',
                                    gap: 14,
                                }}
                            >
                                <div
                                    className="bg-[var(--cpm-surface-card)] border-[1.5px] border-[var(--cpm-brand-navy)] flex flex-col"
                                    style={{
                                        borderRadius: 16,
                                        padding: '18px 20px',
                                        gap: 14,
                                        boxShadow:
                                            '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                    }}
                                >
                                    <div className="flex items-center" style={{ gap: 8 }}>
                                        <span
                                            className="inline-flex items-center rounded-full bg-[var(--cpm-brand-navy)] text-white uppercase"
                                            style={{
                                                padding: '3px 9px',
                                                fontSize: 10,
                                                fontWeight: 800,
                                                letterSpacing: '0.06em',
                                            }}
                                        >
                                            {isLive ? 'Live record' : 'New record'}
                                        </span>
                                        <CpmStatusChip status={pendingItem.status} />
                                    </div>
                                    <div
                                        className="text-[var(--cpm-text-primary)]"
                                        style={{
                                            fontSize: 19,
                                            fontWeight: 800,
                                            letterSpacing: '-0.02em',
                                            lineHeight: 1.25,
                                        }}
                                    >
                                        {pendingItem.name}
                                    </div>
                                    <div className="flex flex-col border-t border-[var(--cpm-border-default)]">
                                        {fieldRow('Brand', pendingItem.brand?.name ?? '—')}
                                        {fieldRow('Volume', packLabel(pendingItem) ?? '—')}
                                        {pendingItem.level === 'variant'
                                            ? fieldRow(
                                                  'Container',
                                                  pendingItem.containerType || 'Not recorded'
                                              )
                                            : null}
                                        {fieldRow(
                                            'Created by',
                                            formatCreatedVia(pendingItem.provenance?.createdBy)
                                        )}
                                        {fieldRow(
                                            'Created on',
                                            formatFlaggedDate(pendingItem.createdAt)
                                        )}
                                    </div>
                                    <div
                                        className="flex flex-col border-t border-[var(--cpm-border-default)]"
                                        style={{ gap: 7, paddingTop: 11 }}
                                    >
                                        <span
                                            className="uppercase text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                fontSize: 10.5,
                                                fontWeight: 800,
                                                letterSpacing: '0.06em',
                                            }}
                                        >
                                            Known aliases
                                        </span>
                                        <div className="flex flex-wrap" style={{ gap: 6 }}>
                                            {(pendingItem.aliases ?? []).length > 0 ? (
                                                (pendingItem.aliases ?? []).map((alias, idx) => (
                                                    <span
                                                        key={`${alias}-${idx}`}
                                                        className="inline-flex items-center rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)] text-[var(--cpm-text-primary)]"
                                                        style={{
                                                            padding: '4px 10px',
                                                            fontSize: 11.5,
                                                            fontWeight: 800,
                                                        }}
                                                    >
                                                        {alias}
                                                    </span>
                                                ))
                                            ) : (
                                                <span
                                                    className="text-[var(--cpm-text-tertiary)]"
                                                    style={{ fontSize: 12, fontWeight: 700 }}
                                                >
                                                    None recorded
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {hasDup ? (
                                    <div
                                        className="bg-[var(--cpm-surface-card)] border-[1.5px] border-[var(--cpm-warning-border)] flex flex-col"
                                        style={{
                                            borderRadius: 16,
                                            padding: '18px 20px',
                                            gap: 14,
                                            boxShadow:
                                                '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                        }}
                                    >
                                        <div className="flex items-center" style={{ gap: 8 }}>
                                            <span
                                                className="inline-flex items-center rounded-full bg-[var(--cpm-warning-text)] text-white uppercase"
                                                style={{
                                                    padding: '3px 9px',
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.06em',
                                                }}
                                            >
                                                Suspected duplicate of
                                            </span>
                                            <DuplicateStatusBadge candidate={topDup} />
                                        </div>
                                        <div
                                            className="text-[var(--cpm-text-primary)]"
                                            style={{
                                                fontSize: 19,
                                                fontWeight: 800,
                                                letterSpacing: '-0.02em',
                                                lineHeight: 1.25,
                                            }}
                                        >
                                            {duplicateCandidateLabel(
                                                topDup,
                                                String(pendingItem.possibleDuplicateOf)
                                            )}
                                        </div>
                                        <div className="flex flex-col border-t border-[var(--cpm-border-default)]">
                                            {fieldRow(
                                                'Master id',
                                                String(pendingItem.possibleDuplicateOf)
                                            )}
                                            {duplicateCandidateFieldRows(topDup)}
                                            {fieldRow(
                                                'Candidates',
                                                String(
                                                    pendingItem.possibleDuplicateCandidates
                                                        ?.length ?? 0
                                                )
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            {dupAlts.length > 0 ? (
                                <div className="flex flex-col" style={{ gap: 12 }}>
                                    <button
                                        type="button"
                                        onClick={() => setDupExpanded((v) => !v)}
                                        className="self-start inline-flex items-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-brand-navy)]"
                                        style={{
                                            gap: 8,
                                            height: 34,
                                            paddingInline: 14,
                                            borderRadius: 10,
                                            fontSize: 12.5,
                                            fontWeight: 800,
                                        }}
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="var(--cpm-brand-purple)"
                                            strokeWidth="2"
                                        >
                                            <rect width="7" height="7" x="3" y="3" rx="1" />
                                            <rect width="7" height="7" x="14" y="3" rx="1" />
                                            <rect width="7" height="7" x="14" y="14" rx="1" />
                                            <rect width="7" height="7" x="3" y="14" rx="1" />
                                        </svg>
                                        {dupExpanded
                                            ? 'Hide other possible matches'
                                            : `Compare ${dupAlts.length} more match${dupAlts.length === 1 ? '' : 'es'}`}
                                    </button>
                                    {dupExpanded ? (
                                        <div className="flex flex-col" style={{ gap: 10 }}>
                                            <span
                                                className="text-[var(--cpm-text-secondary)]"
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                The system ranked these lower than the top match,
                                                but any of them could be the real duplicate. Pick
                                                the one that matches to send it to the merge
                                                decision instead of the top pick.
                                            </span>
                                            <div
                                                className="grid"
                                                style={{
                                                    gridTemplateColumns:
                                                        'repeat(auto-fit, minmax(280px, 1fr))',
                                                    gap: 12,
                                                }}
                                            >
                                                {dupAlts.map((alt) => (
                                                    <div
                                                        key={alt.masterId}
                                                        className="flex flex-col bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)]"
                                                        style={{
                                                            gap: 11,
                                                            padding: '15px 16px',
                                                            borderRadius: 14,
                                                            boxShadow:
                                                                '0 1px 2px rgba(14,8,88,.04)',
                                                        }}
                                                    >
                                                        <div
                                                            className="flex items-center"
                                                            style={{ gap: 8 }}
                                                        >
                                                            <DuplicateStatusBadge candidate={alt} />
                                                            <span
                                                                className="ml-auto text-[var(--cpm-text-tertiary)]"
                                                                style={{
                                                                    fontSize: 10.5,
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {Math.round((alt.score ?? 0) * 100)}
                                                                % match
                                                            </span>
                                                        </div>
                                                        <div
                                                            className="text-[var(--cpm-text-primary)]"
                                                            style={{
                                                                fontSize: 15,
                                                                fontWeight: 800,
                                                                letterSpacing: '-0.015em',
                                                                lineHeight: 1.25,
                                                            }}
                                                        >
                                                            {duplicateCandidateLabel(
                                                                alt,
                                                                alt.masterId
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col border-t border-[var(--cpm-border-default)]">
                                                            {fieldRow('Master id', alt.masterId)}
                                                            {duplicateCandidateFieldRows(alt)}
                                                            {fieldRow(
                                                                'Score',
                                                                (alt.score ?? 0).toFixed(2)
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => openMerge(alt.masterId)}
                                                            className="inline-flex items-center justify-center bg-[var(--cpm-warning-bg)] border-[1.5px] border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)]"
                                                            style={{
                                                                gap: 7,
                                                                height: 38,
                                                                marginTop: 2,
                                                                borderRadius: 10,
                                                                fontSize: 12.5,
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            Merge with this instead
                                                            <svg
                                                                width="13"
                                                                height="13"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2.4"
                                                            >
                                                                <path d="M5 12h14" />
                                                                <path d="m12 5 7 7-7 7" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>

                        <div
                            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
                            style={{
                                borderRadius: 16,
                                padding: '18px 20px',
                                gap: 14,
                                boxShadow:
                                    '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                            }}
                        >
                            <span
                                className="uppercase text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                            >
                                Why this record exists
                            </span>
                            <div
                                className="grid"
                                style={{
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                    gap: 14,
                                }}
                            >
                                <div
                                    className="flex bg-[var(--cpm-surface-subtle)]"
                                    style={{ gap: 11, padding: '14px 16px', borderRadius: 10 }}
                                >
                                    <span
                                        className="shrink-0 inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                        style={{ width: 30, height: 30, borderRadius: 8 }}
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M12 8V4H8" />
                                            <rect width="16" height="12" x="4" y="8" rx="2" />
                                        </svg>
                                    </span>
                                    <div className="flex flex-col" style={{ gap: 3 }}>
                                        <span
                                            className="text-[var(--cpm-text-primary)]"
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {formatCreatedVia(pendingItem.provenance?.createdBy)}
                                        </span>
                                        <span
                                            className="text-[var(--cpm-text-secondary)]"
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            The pipeline created a new master because no confident
                                            existing match was found.
                                        </span>
                                    </div>
                                </div>
                                {hasDup ? (
                                    <div
                                        className="flex bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)]"
                                        style={{ gap: 11, padding: '14px 16px', borderRadius: 10 }}
                                    >
                                        <span
                                            className="shrink-0 inline-flex items-center justify-center bg-white border border-[var(--cpm-warning-border)] text-[var(--cpm-warning-icon)]"
                                            style={{ width: 30, height: 30, borderRadius: 8 }}
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <rect width="14" height="14" x="8" y="8" rx="2" />
                                                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                            </svg>
                                        </span>
                                        <div className="flex flex-col" style={{ gap: 3 }}>
                                            <span
                                                className="text-[var(--cpm-warning-text)]"
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.01em',
                                                }}
                                            >
                                                Flagged as a possible duplicate
                                            </span>
                                            <span
                                                className="text-[var(--cpm-warning-text-med)]"
                                                style={{
                                                    fontSize: 12.5,
                                                    fontWeight: 600,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                Name similarity flagged this against an active
                                                catalog record. Approve as-is, or open a merge
                                                decision from the footer.
                                            </span>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div
                            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
                            style={{
                                borderRadius: 16,
                                padding: '18px 20px',
                                gap: 14,
                                boxShadow:
                                    '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                            }}
                        >
                            <div className="flex items-start" style={{ gap: 12 }}>
                                <span
                                    className="shrink-0 inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                    style={{ width: 30, height: 30, borderRadius: 8 }}
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="m21 21-4.3-4.3" />
                                    </svg>
                                </span>
                                <div className="flex flex-col min-w-0" style={{ gap: 3 }}>
                                    <span
                                        className="text-[var(--cpm-text-primary)]"
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 800,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        Think the automated check missed a duplicate?
                                    </span>
                                    <span
                                        className="text-[var(--cpm-text-secondary)]"
                                        style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                                    >
                                        The pipeline only clusters records whose names read alike,
                                        so a real duplicate with a different wording can slip
                                        through. Search the live catalog yourself — if you recognise
                                        this as an existing brand, product or variant, you can route
                                        it into a merge even though it was never auto-flagged.
                                    </span>
                                </div>
                            </div>
                            <div
                                className="flex items-center bg-white border-[1.5px] border-[var(--cpm-border-strong)]"
                                style={{ gap: 8, height: 42, paddingInline: 13, borderRadius: 10 }}
                            >
                                {searchLoading ? (
                                    <span
                                        className="shrink-0 inline-block border-2 border-[var(--cpm-border-strong)] border-t-[var(--cpm-brand-purple)] rounded-full animate-spin"
                                        style={{ width: 14, height: 14 }}
                                        aria-hidden
                                    />
                                ) : (
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="var(--cpm-text-tertiary)"
                                        strokeWidth="2"
                                        className="shrink-0"
                                    >
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="m21 21-4.3-4.3" />
                                    </svg>
                                )}
                                <input
                                    value={search}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setSearch(next);
                                        if (manualDup) setManualDup(null);
                                    }}
                                    placeholder={catalogSearchPlaceholder(searchLevel)}
                                    className="flex-1 min-w-0 border-0 outline-none bg-transparent text-[var(--cpm-text-primary)]"
                                    style={{ fontSize: 13, fontWeight: 700 }}
                                />
                                {search.trim() ? (
                                    <button
                                        type="button"
                                        onClick={clearCatalogSearch}
                                        className="shrink-0 inline-flex items-center justify-center text-[var(--cpm-text-tertiary)] hover:text-[var(--cpm-text-secondary)]"
                                        style={{ width: 22, height: 22 }}
                                        aria-label="Clear search"
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                        >
                                            <path d="M18 6 6 18" />
                                            <path d="m6 6 12 12" />
                                        </svg>
                                    </button>
                                ) : null}
                            </div>
                            {manualDup ? (
                                <div
                                    className="flex flex-wrap items-center bg-[var(--cpm-warning-bg)] border-[1.5px] border-[var(--cpm-warning-border)]"
                                    style={{ gap: 12, padding: '13px 15px', borderRadius: 10 }}
                                    title={manualDup._id}
                                >
                                    <span
                                        className="shrink-0 inline-flex items-center justify-center bg-white border border-[var(--cpm-warning-border)] text-[var(--cpm-warning-icon)]"
                                        style={{ width: 30, height: 30, borderRadius: 8 }}
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <rect width="14" height="14" x="8" y="8" rx="2" />
                                            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                        </svg>
                                    </span>
                                    <div
                                        className="flex flex-col min-w-[180px] flex-1"
                                        style={{ gap: 2 }}
                                    >
                                        <span
                                            className="text-[var(--cpm-warning-text)]"
                                            style={{ fontSize: 13, fontWeight: 800 }}
                                        >
                                            Matched against {manualDup.name}
                                        </span>
                                        <span
                                            className="text-[var(--cpm-warning-text-med)]"
                                            style={{ fontSize: 12, fontWeight: 600 }}
                                        >
                                            {manualDup.meta}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setManualDup(null)}
                                        className="text-[var(--cpm-warning-text-med)]"
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 800,
                                            borderBottom: '1.5px solid var(--cpm-warning-border)',
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div>
                            ) : null}
                            {!manualDup && search.trim().length >= 2 && !searchLoading ? (
                                searchResults.length > 0 ? (
                                    <div className="border border-[var(--cpm-border-default)] rounded-[10px] overflow-hidden">
                                        {searchResults.map((r) => (
                                            <div
                                                key={r._id}
                                                className="flex items-center justify-between bg-white border-b border-[var(--cpm-border-default)] last:border-b-0"
                                                style={{ gap: 12, padding: '11px 14px' }}
                                                title={r._id}
                                            >
                                                <div
                                                    className="min-w-0 flex flex-col"
                                                    style={{ gap: 4 }}
                                                >
                                                    <div
                                                        className="flex items-center min-w-0"
                                                        style={{ gap: 8 }}
                                                    >
                                                        <span
                                                            className="truncate text-[var(--cpm-text-primary)]"
                                                            style={{
                                                                fontSize: 13.5,
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {r.name}
                                                        </span>
                                                        <CpmTypeChip recordType={r.level} />
                                                    </div>
                                                    <span
                                                        className="truncate text-[var(--cpm-text-tertiary)]"
                                                        style={{ fontSize: 11.5, fontWeight: 600 }}
                                                    >
                                                        {r.meta}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setManualDup({
                                                            _id: r._id,
                                                            name: r.name,
                                                            meta: r.meta,
                                                        });
                                                        setSearchResults([]);
                                                    }}
                                                    className="shrink-0 inline-flex items-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-brand-navy)]"
                                                    style={{
                                                        gap: 6,
                                                        height: 34,
                                                        paddingInline: 13,
                                                        borderRadius: 9,
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                    }}
                                                >
                                                    This is the same →
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div
                                        className="border border-[var(--cpm-border-default)] rounded-[10px] text-[var(--cpm-text-tertiary)]"
                                        style={{
                                            padding: '12px 14px',
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                        }}
                                    >
                                        No matches for &ldquo;{search.trim()}&rdquo;
                                    </div>
                                )
                            ) : null}
                        </div>
                    </>
                ) : null}

                {/* ─── LINK ─── */}
                {mode === 'link' && linkItem ? (
                    <>
                        <div className="flex flex-col" style={{ gap: 12 }}>
                            <div className="flex items-baseline" style={{ gap: 12 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    Comparison
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 13, fontWeight: 600 }}
                                >
                                    Does the tenant&apos;s record belong to the master product the
                                    AI picked?
                                </span>
                            </div>
                            <div
                                className="grid items-stretch"
                                style={{
                                    gridTemplateColumns: 'minmax(280px,0.95fr) 34px minmax(0,2fr)',
                                    gap: 0,
                                }}
                            >
                                <div
                                    className="bg-[var(--cpm-surface-card)] border-[1.5px] border-[var(--cpm-brand-navy)] flex flex-col"
                                    style={{
                                        borderRadius: 16,
                                        padding: 18,
                                        gap: 14,
                                        boxShadow:
                                            '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                    }}
                                >
                                    <span
                                        className="inline-flex self-start items-center rounded-full bg-[var(--cpm-brand-navy)] text-white uppercase"
                                        style={{
                                            padding: '3px 9px',
                                            fontSize: 10,
                                            fontWeight: 800,
                                            letterSpacing: '0.06em',
                                        }}
                                    >
                                        Tenant record
                                    </span>
                                    <div
                                        className="text-[var(--cpm-text-primary)]"
                                        style={{
                                            fontSize: 17,
                                            fontWeight: 800,
                                            letterSpacing: '-0.02em',
                                            lineHeight: 1.25,
                                        }}
                                    >
                                        {linkItem.name?.trim() || linkItem.sourceId}
                                    </div>
                                    <div className="flex flex-col border-t border-[var(--cpm-border-default)]">
                                        {fieldRow(
                                            'Type',
                                            recordTypeLabel(
                                                sourceTypeToRecordType(linkItem.sourceType)
                                            )
                                        )}
                                        {fieldRow('Volume', packLabel(linkItem) ?? '—')}
                                        {fieldRow(
                                            'Store brand',
                                            linkItem.storeBrandName || linkItem.storeBrand || '—'
                                        )}
                                        {fieldRow('Method', linkItem.method)}
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <span
                                        className="inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                        style={{ width: 30, height: 30, borderRadius: 8 }}
                                    >
                                        <svg
                                            width="15"
                                            height="15"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                        >
                                            <path d="M5 12h14" />
                                            <path d="m12 5 7 7-7 7" />
                                        </svg>
                                    </span>
                                </div>
                                <div
                                    className="grid"
                                    style={{
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                                        gap: 14,
                                    }}
                                >
                                    {linkCandidates.map((c, idx) => {
                                        const selected =
                                            linkIntent !== 'new' &&
                                            (chosenId === c.productVariantId ||
                                                (!selectedMasterId && idx === 0));
                                        const pct = Math.round((c.compositeConfidence ?? 0) * 100);
                                        const volumeText =
                                            packLabel({
                                                volumeMl: c.volumeMl ?? undefined,
                                                volumeUnit: c.volumeUnit ?? undefined,
                                            }) ?? '—';
                                        const tenantVolume = packLabel(linkItem) ?? '';
                                        const volumeWarn =
                                            tenantVolume !== '' &&
                                            volumeText !== '—' &&
                                            volumeText !== tenantVolume;
                                        const viewHref = masterEditorRecordHref(
                                            linkItem.masterType,
                                            c.productVariantId
                                        );
                                        return (
                                            <button
                                                key={c.productVariantId}
                                                type="button"
                                                onClick={() =>
                                                    pickCandidate(c.productVariantId, idx === 0)
                                                }
                                                className="text-left flex flex-col bg-[var(--cpm-surface-card)] border-[1.5px]"
                                                style={{
                                                    borderRadius: 16,
                                                    padding: 16,
                                                    gap: 10,
                                                    borderColor: selected
                                                        ? 'var(--cpm-brand-purple)'
                                                        : 'var(--cpm-border-default)',
                                                    boxShadow: selected
                                                        ? '0 0 0 3px rgba(110,100,240,.28), 0 12px 32px -8px rgba(14,8,88,.16)'
                                                        : '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                                }}
                                            >
                                                <div
                                                    className="flex items-center min-h-[22px]"
                                                    style={{ gap: 8 }}
                                                >
                                                    <span
                                                        className="inline-flex items-center rounded-full uppercase"
                                                        style={{
                                                            padding: '3px 9px',
                                                            fontSize: 10,
                                                            fontWeight: 800,
                                                            letterSpacing: '0.06em',
                                                            background:
                                                                idx === 0
                                                                    ? 'var(--cpm-brand-purple)'
                                                                    : 'var(--cpm-surface-skeleton)',
                                                            color:
                                                                idx === 0
                                                                    ? '#fff'
                                                                    : 'var(--cpm-text-secondary)',
                                                        }}
                                                    >
                                                        {idx === 0
                                                            ? 'AI\u2019S PICK'
                                                            : 'ALTERNATIVE'}
                                                    </span>
                                                    <span
                                                        className="ml-auto inline-flex items-center justify-center rounded-full"
                                                        style={{
                                                            width: 18,
                                                            height: 18,
                                                            background: selected
                                                                ? 'var(--cpm-brand-purple)'
                                                                : 'transparent',
                                                            border: `1.5px solid ${selected ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                                                        }}
                                                    >
                                                        {selected ? (
                                                            <svg
                                                                width="11"
                                                                height="11"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="#fff"
                                                                strokeWidth="3.2"
                                                            >
                                                                <path d="M20 6 9 17l-5-5" />
                                                            </svg>
                                                        ) : null}
                                                    </span>
                                                </div>
                                                <div
                                                    className="text-[var(--cpm-text-primary)]"
                                                    style={{
                                                        height: 39,
                                                        overflow: 'hidden',
                                                        fontSize: 15,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.015em',
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {c.name}
                                                </div>
                                                <div
                                                    className="flex items-center"
                                                    style={{ gap: 8 }}
                                                >
                                                    {c.status ? (
                                                        <CpmStatusChip status={c.status} />
                                                    ) : null}
                                                    <a
                                                        href={viewHref}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-[var(--cpm-text-tertiary)] hover:text-[var(--cpm-brand-purple)]"
                                                        style={{
                                                            fontSize: 10.5,
                                                            fontWeight: 700,
                                                            textDecoration: 'none',
                                                        }}
                                                    >
                                                        View record
                                                    </a>
                                                </div>
                                                <div className="flex flex-col border-t border-[var(--cpm-border-default)]">
                                                    <div
                                                        className="flex items-center justify-between"
                                                        style={{
                                                            gap: 10,
                                                            padding: '7px 0',
                                                            borderBottom:
                                                                '1px solid var(--cpm-border-default)',
                                                        }}
                                                    >
                                                        <span
                                                            className="uppercase text-[var(--cpm-text-tertiary)]"
                                                            style={{
                                                                fontSize: 10.5,
                                                                fontWeight: 800,
                                                                letterSpacing: '0.06em',
                                                            }}
                                                        >
                                                            Brand
                                                        </span>
                                                        <span
                                                            className="text-[var(--cpm-text-primary)]"
                                                            style={{
                                                                fontSize: 12.5,
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {c.brand?.trim() || '—'}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="flex items-center justify-between"
                                                        style={{ gap: 10, padding: '7px 0' }}
                                                    >
                                                        <span
                                                            className="uppercase text-[var(--cpm-text-tertiary)]"
                                                            style={{
                                                                fontSize: 10.5,
                                                                fontWeight: 800,
                                                                letterSpacing: '0.06em',
                                                            }}
                                                        >
                                                            Volume
                                                        </span>
                                                        <span
                                                            className="tabular"
                                                            style={{
                                                                fontSize: 12.5,
                                                                fontWeight: 800,
                                                                color: volumeWarn
                                                                    ? 'var(--cpm-warning-text-med)'
                                                                    : 'var(--cpm-text-primary)',
                                                            }}
                                                        >
                                                            {volumeText}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div
                                                    className="flex items-center justify-between"
                                                    style={{ marginTop: 'auto', paddingTop: 10 }}
                                                >
                                                    <span
                                                        className="uppercase text-[var(--cpm-text-tertiary)]"
                                                        style={{
                                                            fontSize: 10.5,
                                                            fontWeight: 800,
                                                            letterSpacing: '0.06em',
                                                        }}
                                                    >
                                                        Overall match
                                                    </span>
                                                    <span
                                                        className="tabular"
                                                        style={{
                                                            fontSize: 19,
                                                            fontWeight: 800,
                                                            letterSpacing: '-0.02em',
                                                            color:
                                                                idx === 0
                                                                    ? 'var(--cpm-brand-navy)'
                                                                    : 'var(--cpm-text-secondary)',
                                                        }}
                                                    >
                                                        {pct}%
                                                    </span>
                                                </div>
                                                <div
                                                    className="rounded-full bg-[var(--cpm-surface-skeleton)] overflow-hidden"
                                                    style={{ height: 5 }}
                                                >
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{
                                                            width: `${pct}%`,
                                                            background:
                                                                idx === 0
                                                                    ? 'var(--cpm-brand-purple)'
                                                                    : 'var(--cpm-text-tertiary)',
                                                        }}
                                                    />
                                                </div>
                                            </button>
                                        );
                                    })}
                                    {!linkCandidates.length ? (
                                        <div className="text-[13px] text-[var(--cpm-text-secondary)] p-4">
                                            No candidates in matchTrace.
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div
                            className="grid items-start"
                            style={{
                                gridTemplateColumns: 'minmax(0,1.55fr) minmax(320px,0.9fr)',
                                gap: 16,
                            }}
                        >
                            <div
                                className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
                                style={{
                                    borderRadius: 16,
                                    padding: '18px 20px 14px',
                                    gap: 14,
                                    boxShadow:
                                        '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                }}
                            >
                                <div className="flex flex-col" style={{ gap: 4 }}>
                                    <span
                                        className="uppercase text-[var(--cpm-text-secondary)]"
                                        style={{
                                            fontSize: 10.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.08em',
                                        }}
                                    >
                                        Why the AI picked this
                                    </span>
                                    <span
                                        className="text-[var(--cpm-text-secondary)]"
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        Each check below adds to — or withholds from — the overall
                                        match score. Signals that did not fire contributed nothing.
                                    </span>
                                </div>
                                {aiPick ? (
                                    <div className="flex flex-col">
                                        {CPM_MATCH_SIGNAL_ROWS.map((row, signalIdx) => {
                                            const raw = aiPickSignals[row.key];
                                            const value =
                                                raw != null && !Number.isNaN(Number(raw))
                                                    ? Math.max(0, Math.min(1, Number(raw)))
                                                    : 0;
                                            const fired = value > 0;
                                            return (
                                                <div
                                                    key={row.key}
                                                    className="grid items-center"
                                                    style={{
                                                        gridTemplateColumns:
                                                            'minmax(140px,0.9fr) minmax(0,1.4fr) 96px 48px',
                                                        gap: 12,
                                                        padding: '11px 0',
                                                        borderBottom:
                                                            signalIdx <
                                                            CPM_MATCH_SIGNAL_ROWS.length - 1
                                                                ? '1px solid var(--cpm-border-default)'
                                                                : undefined,
                                                        opacity: fired ? 1 : 0.72,
                                                    }}
                                                >
                                                    <div
                                                        className="flex flex-col min-w-0"
                                                        style={{ gap: 3 }}
                                                    >
                                                        <span
                                                            style={{
                                                                fontSize: 12.5,
                                                                fontWeight: 800,
                                                                color: fired
                                                                    ? 'var(--cpm-text-primary)'
                                                                    : 'var(--cpm-text-secondary)',
                                                            }}
                                                        >
                                                            {row.label}
                                                        </span>
                                                        <span
                                                            className="font-[family-name:var(--cpm-font-mono)] text-[var(--cpm-text-tertiary)]"
                                                            style={{
                                                                fontSize: 10.5,
                                                                fontWeight: 700,
                                                                letterSpacing: '0.05em',
                                                            }}
                                                        >
                                                            {row.code}
                                                        </span>
                                                    </div>
                                                    <span
                                                        className="text-[var(--cpm-text-secondary)]"
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            lineHeight: 1.45,
                                                        }}
                                                    >
                                                        {row.plain}
                                                    </span>
                                                    <div
                                                        className="rounded-full bg-[var(--cpm-surface-skeleton)] overflow-hidden"
                                                        style={{ width: 96, height: 6 }}
                                                    >
                                                        <div
                                                            className="h-full rounded-full"
                                                            style={{
                                                                width: `${Math.round(value * 100)}%`,
                                                                background: fired
                                                                    ? 'var(--cpm-brand-purple)'
                                                                    : 'var(--cpm-text-tertiary)',
                                                            }}
                                                        />
                                                    </div>
                                                    <span
                                                        className="tabular text-right"
                                                        style={{
                                                            fontSize: 12.5,
                                                            fontWeight: 800,
                                                            color: fired
                                                                ? 'var(--cpm-brand-navy)'
                                                                : 'var(--cpm-text-tertiary)',
                                                        }}
                                                    >
                                                        {value.toFixed(2)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        <div
                                            className="flex items-center justify-between"
                                            style={{
                                                gap: 12,
                                                padding: '13px 0 4px',
                                                borderTop: '1.5px solid var(--cpm-border-strong)',
                                                marginTop: 6,
                                            }}
                                        >
                                            <span
                                                className="text-[var(--cpm-text-primary)]"
                                                style={{
                                                    fontSize: 12.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.01em',
                                                }}
                                            >
                                                Overall match score for the AI&apos;s pick
                                            </span>
                                            <span
                                                className="tabular text-[var(--cpm-brand-navy)]"
                                                style={{
                                                    fontSize: 20,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.02em',
                                                }}
                                            >
                                                {aiPickScore != null ? aiPickScore.toFixed(2) : '—'}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <p
                                        className="m-0 text-[var(--cpm-text-secondary)]"
                                        style={{
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        Matched deterministically — exact identifier match, no
                                        scoring required.
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col" style={{ gap: 16 }}>
                                {linkItem.matchTrace?.nearDuplicateClusterDetected ? (
                                    <div
                                        className="bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)] flex flex-col"
                                        style={{
                                            borderRadius: 16,
                                            padding: '16px 18px',
                                            gap: 10,
                                        }}
                                    >
                                        <div className="flex items-center" style={{ gap: 9 }}>
                                            <svg
                                                width="17"
                                                height="17"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="var(--cpm-warning-icon)"
                                                strokeWidth="2"
                                            >
                                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                                <path d="M12 9v4" />
                                                <path d="M12 17h.01" />
                                            </svg>
                                            <span
                                                className="text-[var(--cpm-warning-text)]"
                                                style={{ fontSize: 13.5, fontWeight: 800 }}
                                            >
                                                Near-duplicate warning fired
                                            </span>
                                        </div>
                                        <p
                                            className="m-0 text-[var(--cpm-warning-text-med)]"
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                lineHeight: 1.55,
                                            }}
                                        >
                                            Two candidates share almost the same words, so the pick
                                            could be the wrong one of a close pair. Check the volume
                                            before you accept.
                                        </p>
                                        <div
                                            className="flex items-center bg-white border border-[var(--cpm-warning-border)]"
                                            style={{
                                                gap: 8,
                                                padding: '9px 11px',
                                                borderRadius: 10,
                                            }}
                                        >
                                            <span
                                                className="font-[family-name:var(--cpm-font-mono)] text-[var(--cpm-text-tertiary)]"
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.05em',
                                                }}
                                            >
                                                NEAR_DUPLICATE_TOKEN_CLUSTER
                                            </span>
                                        </div>
                                        {nearDupPeerName ? (
                                            <span
                                                className="text-[var(--cpm-warning-text)]"
                                                style={{ fontSize: 12, fontWeight: 700 }}
                                            >
                                                Clustered against: {nearDupPeerName}
                                            </span>
                                        ) : null}
                                    </div>
                                ) : null}

                                <div
                                    className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
                                    style={{
                                        borderRadius: 16,
                                        padding: '16px 18px',
                                        gap: 10,
                                        boxShadow:
                                            '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                    }}
                                >
                                    <span
                                        className="uppercase text-[var(--cpm-text-secondary)]"
                                        style={{
                                            fontSize: 10.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.08em',
                                        }}
                                    >
                                        Who made this call
                                    </span>
                                    <div className="flex items-center" style={{ gap: 11 }}>
                                        <span
                                            className="shrink-0 inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                            style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: 8,
                                            }}
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M12 8V4H8" />
                                                <rect width="16" height="12" x="4" y="8" rx="2" />
                                                <path d="M2 14h2" />
                                                <path d="M20 14h2" />
                                                <path d="M15 13v2" />
                                                <path d="M9 13v2" />
                                            </svg>
                                        </span>
                                        <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
                                            <span
                                                className="text-[var(--cpm-text-primary)]"
                                                style={{
                                                    fontSize: 13.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.01em',
                                                }}
                                            >
                                                {linkModelCopy.line}
                                            </span>
                                            <span
                                                className="text-[var(--cpm-text-secondary)]"
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    lineHeight: 1.45,
                                                }}
                                            >
                                                {linkModelCopy.explain}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        className="flex items-center justify-between"
                                        style={{
                                            gap: 10,
                                            paddingTop: 10,
                                            borderTop: '1px solid var(--cpm-border-default)',
                                        }}
                                    >
                                        <span
                                            className="uppercase text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                fontSize: 10.5,
                                                fontWeight: 800,
                                                letterSpacing: '0.06em',
                                            }}
                                        >
                                            Method / band
                                        </span>
                                        <span
                                            className="font-[family-name:var(--cpm-font-mono)] text-[var(--cpm-text-primary)]"
                                            style={{ fontSize: 12, fontWeight: 800 }}
                                        >
                                            {[
                                                linkItem.method || '—',
                                                linkItem.confidenceBand || null,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}

                {/* ─── BARCODE ─── */}
                {mode === 'barcode' && barcodeItem ? (
                    <>
                        <div className="flex flex-col" style={{ gap: 12 }}>
                            <div className="flex items-baseline" style={{ gap: 12 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    Comparison
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 13, fontWeight: 600 }}
                                >
                                    {isBarcodeConflicted
                                        ? `${barcodeClaimants.length} master products are claiming this barcode. Which one does it really belong to?`
                                        : 'One trusted owner — and store brands that have stopped reporting it.'}
                                </span>
                            </div>
                            <div
                                className="grid"
                                style={{
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
                                    gap: 16,
                                }}
                            >
                                {barcodeClaimants.map((entry) => {
                                    const selected =
                                        String(winningVariantId) === String(entry.productVariant);
                                    const reports = reportRows(entry);
                                    const strong = reports.length > 1;
                                    const good = !isBarcodeConflicted || strong;
                                    return (
                                        <div
                                            key={entry._id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                setWinningVariantId(String(entry.productVariant))
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    setWinningVariantId(
                                                        String(entry.productVariant)
                                                    );
                                                }
                                            }}
                                            className="flex flex-col bg-[var(--cpm-surface-card)] cursor-pointer"
                                            style={{
                                                gap: 12,
                                                padding: '18px 20px',
                                                borderRadius: 16,
                                                border: `1.5px solid ${selected ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-default)'}`,
                                                boxShadow: selected
                                                    ? '0 0 0 3px rgba(110,100,240,.28), 0 12px 32px -8px rgba(14,8,88,.16)'
                                                    : '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                            }}
                                        >
                                            <div className="flex items-center" style={{ gap: 8 }}>
                                                <span
                                                    className="inline-flex items-center rounded-full uppercase border"
                                                    style={{
                                                        padding: '3px 9px',
                                                        fontSize: 10,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.06em',
                                                        background: good
                                                            ? 'var(--cpm-success-bg)'
                                                            : 'var(--cpm-warning-bg)',
                                                        borderColor: good
                                                            ? 'var(--cpm-success-border)'
                                                            : 'var(--cpm-warning-border)',
                                                        color: good
                                                            ? 'var(--cpm-success-text)'
                                                            : 'var(--cpm-warning-text)',
                                                    }}
                                                >
                                                    {!isBarcodeConflicted
                                                        ? 'Trusted owner'
                                                        : strong
                                                          ? 'Corroborated'
                                                          : 'Single report'}
                                                </span>
                                                <span
                                                    className="ml-auto inline-flex items-center justify-center rounded-full"
                                                    style={{
                                                        width: 18,
                                                        height: 18,
                                                        background: selected
                                                            ? 'var(--cpm-brand-purple)'
                                                            : 'transparent',
                                                        border: `1.5px solid ${selected ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                                                    }}
                                                >
                                                    {selected ? (
                                                        <svg
                                                            width="11"
                                                            height="11"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="#fff"
                                                            strokeWidth="3.2"
                                                        >
                                                            <path d="M20 6 9 17l-5-5" />
                                                        </svg>
                                                    ) : null}
                                                </span>
                                            </div>
                                            <div
                                                className="text-[var(--cpm-text-primary)]"
                                                style={{
                                                    fontSize: 18,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.02em',
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                {claimantLabel(entry)}
                                            </div>
                                            <div
                                                className="flex items-center bg-[var(--cpm-surface-subtle)]"
                                                style={{
                                                    gap: 14,
                                                    padding: '10px 12px',
                                                    borderRadius: 10,
                                                }}
                                            >
                                                <span
                                                    className="tabular"
                                                    style={{
                                                        fontSize: 26,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.025em',
                                                        color: good
                                                            ? 'var(--cpm-success-text)'
                                                            : 'var(--cpm-warning-text)',
                                                    }}
                                                >
                                                    {reports.length}
                                                </span>
                                                <span
                                                    className="text-[var(--cpm-text-secondary)]"
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {reports.length === 1
                                                        ? 'store brand reported this barcode against this product'
                                                        : 'store brands independently reported this barcode against this product'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                {reports.map((r, i) => (
                                                    <div
                                                        key={`${r.tenant}-${i}`}
                                                        className="flex items-start justify-between border-t border-[var(--cpm-border-default)]"
                                                        style={{ gap: 12, padding: '9px 0' }}
                                                    >
                                                        <span
                                                            className="inline-flex items-center text-[var(--cpm-text-primary)]"
                                                            style={{
                                                                gap: 8,
                                                                fontSize: 13,
                                                                fontWeight: 800,
                                                                paddingTop: 1,
                                                            }}
                                                        >
                                                            <span
                                                                className="rounded-full bg-[var(--cpm-brand-purple)]"
                                                                style={{
                                                                    width: 7,
                                                                    height: 7,
                                                                    boxShadow:
                                                                        '0 0 0 3px var(--cpm-purple-tint-13)',
                                                                }}
                                                            />
                                                            {r.tenant}
                                                        </span>
                                                        <span
                                                            className="flex flex-col items-end shrink-0 text-right"
                                                            style={{ gap: 2 }}
                                                        >
                                                            <span
                                                                className="tabular text-[var(--cpm-text-secondary)]"
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                First reported {r.when}
                                                            </span>
                                                            <span
                                                                className={`tabular ${r.hasSold ? 'text-[var(--cpm-text-tertiary)]' : 'text-[var(--cpm-warning-text)]'}`}
                                                                style={{
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {r.soldLabel}
                                                            </span>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div
                                                className="inline-flex items-center justify-center"
                                                style={{
                                                    height: 40,
                                                    marginTop: 4,
                                                    borderRadius: 10,
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    background: selected
                                                        ? 'var(--cpm-brand-navy)'
                                                        : '#fff',
                                                    color: selected
                                                        ? '#fff'
                                                        : 'var(--cpm-brand-navy)',
                                                    border: `1.5px solid ${selected ? 'var(--cpm-brand-navy)' : 'var(--cpm-border-strong)'}`,
                                                }}
                                            >
                                                {isBarcodeConflicted
                                                    ? 'Assign barcode to this product'
                                                    : 'Keep this barcode with this product'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {barcodeDropped.length > 0 ? (
                            <div
                                className="bg-[var(--cpm-surface-card)] border-[1.5px] border-[var(--cpm-warning-border)] flex flex-col"
                                style={{
                                    borderRadius: 16,
                                    padding: '18px 20px',
                                    gap: 12,
                                    boxShadow:
                                        '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                                }}
                            >
                                <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                                    <span
                                        className="inline-flex items-center rounded-full bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)] uppercase"
                                        style={{
                                            padding: '3px 9px',
                                            fontSize: 10,
                                            fontWeight: 800,
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        Disputed
                                    </span>
                                    <span
                                        className="text-[var(--cpm-brand-navy)]"
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 800,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {barcodeDropped.length} store brands stopped using this
                                        barcode
                                    </span>
                                </div>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                                >
                                    They reported this barcode before and no longer do. The catalog
                                    still trusts it and keeps auto-linking against it — this is a
                                    signal to look at, not a break.
                                </span>
                                <div className="flex flex-col">
                                    {barcodeDropped.map((d, i) => (
                                        <div
                                            key={`${d.tenant}-${i}`}
                                            className="flex items-center justify-between border-t border-[var(--cpm-border-default)]"
                                            style={{ gap: 12, padding: '9px 0' }}
                                        >
                                            <span
                                                className="inline-flex items-center text-[var(--cpm-text-primary)]"
                                                style={{ gap: 8, fontSize: 13, fontWeight: 800 }}
                                            >
                                                <span
                                                    className="rounded-full bg-[var(--cpm-warning-icon)]"
                                                    style={{
                                                        width: 7,
                                                        height: 7,
                                                        boxShadow:
                                                            '0 0 0 3px var(--cpm-warning-bg)',
                                                    }}
                                                />
                                                {d.tenant}
                                            </span>
                                            <span
                                                className="tabular text-[var(--cpm-text-secondary)]"
                                                style={{ fontSize: 12.5, fontWeight: 700 }}
                                            >
                                                Stopped using {d.when}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        <div
                            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
                            style={{
                                borderRadius: 16,
                                padding: '18px 20px',
                                gap: 14,
                                boxShadow:
                                    '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                            }}
                        >
                            <div className="flex flex-col" style={{ gap: 4 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    How the evidence stacks up
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}
                                >
                                    {evidenceLead}
                                </span>
                            </div>
                            <div
                                className="grid"
                                style={{
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                    gap: 14,
                                }}
                            >
                                {evidenceCards.map((e, i) => {
                                    const tone =
                                        e.tone === 'success'
                                            ? {
                                                  bg: 'var(--cpm-success-bg)',
                                                  bd: 'var(--cpm-success-border)',
                                                  title: 'var(--cpm-success-text)',
                                                  body: 'var(--cpm-success-text)',
                                                  icon: 'var(--cpm-success-text-light)',
                                              }
                                            : e.tone === 'warn'
                                              ? {
                                                    bg: 'var(--cpm-warning-bg)',
                                                    bd: 'var(--cpm-warning-border)',
                                                    title: 'var(--cpm-warning-text)',
                                                    body: 'var(--cpm-warning-text-med)',
                                                    icon: 'var(--cpm-warning-icon)',
                                                }
                                              : {
                                                    bg: 'var(--cpm-surface-subtle)',
                                                    bd: 'var(--cpm-border-default)',
                                                    title: 'var(--cpm-brand-navy)',
                                                    body: 'var(--cpm-text-secondary)',
                                                    icon: 'var(--cpm-brand-purple)',
                                                };
                                    return (
                                        <div
                                            key={i}
                                            className="flex"
                                            style={{
                                                gap: 11,
                                                padding: '14px 16px',
                                                borderRadius: 10,
                                                background: tone.bg,
                                                border: `1px solid ${tone.bd}`,
                                            }}
                                        >
                                            <span
                                                className="shrink-0 inline-flex items-center justify-center bg-white"
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: 8,
                                                    border: `1px solid ${tone.bd}`,
                                                    color: tone.icon,
                                                }}
                                            >
                                                {e.tone === 'success' ? (
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                                        <circle cx="9" cy="7" r="4" />
                                                    </svg>
                                                ) : e.tone === 'warn' ? (
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <circle cx="12" cy="12" r="10" />
                                                        <path d="M12 8v4" />
                                                        <path d="M12 16h.01" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <path d="M22 17 13.5 8.5 8.5 13.5 2 7" />
                                                        <path d="M16 17h6v-6" />
                                                    </svg>
                                                )}
                                            </span>
                                            <div className="flex flex-col" style={{ gap: 3 }}>
                                                <span
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 800,
                                                        color: tone.title,
                                                    }}
                                                >
                                                    {e.title}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 600,
                                                        lineHeight: 1.5,
                                                        color: tone.body,
                                                    }}
                                                >
                                                    {e.body}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div
                                className="flex items-center bg-[var(--cpm-surface-subtle)]"
                                style={{ gap: 9, padding: '11px 14px', borderRadius: 10 }}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-text-secondary)"
                                    strokeWidth="2"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4" />
                                    <path d="M12 8h.01" />
                                </svg>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}
                                >
                                    {isBarcodeConflicted
                                        ? 'Assigning a winner updates the master barcode registry. Store brands keep it on their own records until they re-scan, unless you opt in below.'
                                        : 'Keeping it trusted changes nothing. Retiring it stops the catalog auto-linking this barcode — store brands still carrying it keep it until they re-scan, unless you opt in below.'}
                                </span>
                            </div>
                        </div>
                    </>
                ) : null}
            </CpmModalBody>

            <CpmModalFooter layout="stack">
                {mode === 'pending' && pendingItem
                    ? renderVerdictRow(
                          pendingVerdict,
                          isLive ? (
                              <>
                                  <CpmButton variant="outline" size="lg" onClick={onClose}>
                                      Back to the queue
                                  </CpmButton>
                              </>
                          ) : (
                              <>
                                  <CpmButton
                                      variant="navy"
                                      size="lg"
                                      onClick={handleApprove}
                                      disabled={busy || parentBlocked}
                                      isLoading={busy}
                                  >
                                      Approve — this is a real product
                                  </CpmButton>
                                  {(hasDup || manualDup) && mergeTargetId && onOpenMerge ? (
                                      <CpmButton
                                          variant="warn"
                                          size="lg"
                                          onClick={() => openMerge(mergeTargetId)}
                                          disabled={busy}
                                      >
                                          This looks like a duplicate — go to merge
                                      </CpmButton>
                                  ) : null}
                                  <CpmButton
                                      variant="quietDanger"
                                      size="lg"
                                      onClick={() => onOpenReject?.(pendingItem)}
                                      disabled={busy}
                                  >
                                      Reject — not a real product
                                  </CpmButton>
                              </>
                          )
                      )
                    : null}

                {mode === 'link' && linkItem ? (
                    <>
                        {showExcisePicker ? (
                            <div
                                className="flex flex-wrap items-start bg-[var(--cpm-surface-card)] border-[1.5px] border-[var(--cpm-border-strong)]"
                                style={{ gap: 18, padding: '13px 15px', borderRadius: 12 }}
                            >
                                <div
                                    className="flex flex-col min-w-[260px] flex-1"
                                    style={{ gap: 4 }}
                                >
                                    <div className="flex items-center" style={{ gap: 8 }}>
                                        <span
                                            className="text-[var(--cpm-brand-navy)]"
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            Excise category
                                        </span>
                                        <span
                                            className="inline-flex items-center rounded-full"
                                            style={{
                                                padding: '2px 8px',
                                                fontSize: 9.5,
                                                fontWeight: 800,
                                                letterSpacing: '0.06em',
                                                background: exciseRequired
                                                    ? 'var(--cpm-warning-bg)'
                                                    : 'var(--cpm-surface-subtle)',
                                                border: `1px solid ${
                                                    exciseRequired
                                                        ? 'var(--cpm-warning-border)'
                                                        : 'var(--cpm-border-default)'
                                                }`,
                                                color: exciseRequired
                                                    ? 'var(--cpm-warning-text)'
                                                    : 'var(--cpm-text-tertiary)',
                                            }}
                                        >
                                            {exciseRequired
                                                ? CPM_LICENCE_EXCISE_COPY.TAG_REQUIRED
                                                : CPM_LICENCE_EXCISE_COPY.TAG_OPTIONAL}
                                        </span>
                                    </div>
                                    <span
                                        className="text-[var(--cpm-text-secondary)]"
                                        style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
                                    >
                                        {exciseHelper}
                                    </span>
                                    {stateProductExists ? (
                                        <span
                                            className="text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                lineHeight: 1.45,
                                            }}
                                        >
                                            {cpmLicenceExciseInheritText(exciseStateLabel)}
                                        </span>
                                    ) : null}
                                </div>
                                <LicensedExciseDropdown
                                    options={orderedExciseOptions}
                                    selectedId={exciseCategoryId}
                                    onChange={setExciseCategoryId}
                                    blocked={exciseBlocked}
                                    disabled={busy}
                                />
                            </div>
                        ) : null}
                        {correcting && !confirmReject ? (
                            <div
                                className="flex flex-wrap items-center bg-[var(--cpm-purple-tint-5)] border-[1.5px] border-[var(--cpm-purple-accent-border)]"
                                style={{ gap: 14, padding: '11px 14px', borderRadius: 10 }}
                            >
                                <div
                                    className="flex flex-col min-w-[220px] flex-1"
                                    style={{ gap: 2 }}
                                >
                                    <span
                                        className="text-[var(--cpm-brand-navy)]"
                                        style={{ fontSize: 12.5, fontWeight: 800 }}
                                    >
                                        Save this correction for
                                    </span>
                                    <span
                                        className="text-[var(--cpm-text-secondary)]"
                                        style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
                                    >
                                        Teaches the matcher your answer so the same correction
                                        isn&apos;t needed again.
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-center" style={{ gap: 7 }}>
                                    {(
                                        [
                                            {
                                                v: CPM_ALIAS_SCOPE.STORE,
                                                label: 'This store brand only',
                                            },
                                            { v: CPM_ALIAS_SCOPE.GLOBAL, label: 'Whole catalog' },
                                            { v: CPM_ALIAS_SCOPE.BOTH, label: 'Both' },
                                            { v: CPM_ALIAS_SCOPE.NONE, label: 'Don\u2019t save' },
                                        ] as const
                                    ).map((o) => {
                                        const sel = aliasScope === o.v;
                                        return (
                                            <button
                                                key={o.v}
                                                type="button"
                                                onClick={() => setAliasScope(o.v)}
                                                className="inline-flex items-center rounded-full"
                                                style={{
                                                    gap: 7,
                                                    padding: '7px 12px',
                                                    fontSize: 12.5,
                                                    fontWeight: 800,
                                                    background: sel ? '#fff' : 'transparent',
                                                    border: `1.5px solid ${sel ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                                                    color: sel
                                                        ? 'var(--cpm-brand-navy)'
                                                        : 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                <span>{o.label}</span>
                                                <span
                                                    className="font-[family-name:var(--cpm-font-mono)]"
                                                    style={{
                                                        fontSize: 9.5,
                                                        fontWeight: 700,
                                                        letterSpacing: '0.06em',
                                                        color: sel
                                                            ? 'var(--cpm-brand-purple)'
                                                            : 'var(--cpm-text-tertiary)',
                                                    }}
                                                >
                                                    {o.v}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                        {renderVerdictRow(
                            linkVerdict,
                            confirmReject ? (
                                <>
                                    <CpmButton
                                        variant="ghost"
                                        size="lg"
                                        onClick={() => setConfirmReject(false)}
                                        disabled={busy}
                                    >
                                        Keep reviewing
                                    </CpmButton>
                                    <CpmButton
                                        variant="danger"
                                        size="lg"
                                        onClick={handleDismiss}
                                        disabled={busy}
                                        isLoading={busy}
                                    >
                                        Yes, reject this item
                                    </CpmButton>
                                </>
                            ) : (
                                <>
                                    <CpmButton
                                        variant="navy"
                                        size="lg"
                                        onClick={() =>
                                            handleResolve(
                                                linkIntent === 'new'
                                                    ? CPM_QUEUE_RESOLUTION.CREATE_NEW
                                                    : correcting
                                                      ? CPM_QUEUE_RESOLUTION.PICK_DIFFERENT
                                                      : CPM_QUEUE_RESOLUTION.ACCEPT_AI
                                            )
                                        }
                                        disabled={
                                            busy ||
                                            exciseBlocked ||
                                            (linkIntent !== 'new' && !chosenCand) ||
                                            (linkIntent === 'other' && !selectedMasterId)
                                        }
                                        isLoading={busy}
                                        className={
                                            exciseBlocked
                                                ? '!opacity-100 !bg-[var(--cpm-surface-skeleton)] !text-[var(--cpm-text-tertiary)] !shadow-none border-[1.5px] !border-[var(--cpm-border-default)] hover:!brightness-100 hover:!translate-y-0'
                                                : undefined
                                        }
                                    >
                                        {linkIntent === 'new'
                                            ? 'Create new master record'
                                            : correcting && chosenCand
                                              ? `Link to ${chosenCand.name}`
                                              : 'Accept AI\u2019s pick'}
                                    </CpmButton>
                                    <CpmButton
                                        variant="outline"
                                        size="lg"
                                        onClick={() => {
                                            const alt = linkCandidates[1];
                                            if (alt) pickCandidate(alt.productVariantId, false);
                                        }}
                                        disabled={
                                            busy ||
                                            linkCandidates.length < 2 ||
                                            (!!selectedMasterId &&
                                                selectedMasterId !== aiPick?.productVariantId)
                                        }
                                    >
                                        {selectedMasterId &&
                                        selectedMasterId !== aiPick?.productVariantId
                                            ? 'Candidate chosen above'
                                            : 'Choose a different candidate'}
                                    </CpmButton>
                                    <CpmButton
                                        variant={linkIntent === 'new' ? 'navy' : 'outline'}
                                        size="lg"
                                        onClick={toggleNewProduct}
                                        disabled={busy}
                                    >
                                        This is a new product
                                    </CpmButton>
                                    <CpmButton
                                        variant="quietDanger"
                                        size="lg"
                                        onClick={() => setConfirmReject(true)}
                                        disabled={busy}
                                    >
                                        None of these are right — reject
                                    </CpmButton>
                                </>
                            )
                        )}
                    </>
                ) : null}

                {mode === 'barcode' && barcodeItem ? (
                    <>
                        <div
                            className="flex flex-col bg-[var(--cpm-surface-card)] border-[1.5px] border-[var(--cpm-border-strong)]"
                            style={{ gap: 11, padding: '13px 15px', borderRadius: 12 }}
                        >
                            <button
                                type="button"
                                onClick={() => setApplyToTenants((v) => !v)}
                                className="flex items-start text-left w-full bg-transparent border-0 p-0 cursor-pointer"
                                style={{ gap: 11 }}
                            >
                                <span
                                    className="inline-flex items-center justify-center shrink-0"
                                    style={{
                                        width: 18,
                                        height: 18,
                                        marginTop: 1,
                                        borderRadius: 5,
                                        background: applyToTenants
                                            ? 'var(--cpm-brand-purple)'
                                            : '#fff',
                                        border: `1.5px solid ${applyToTenants ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                                    }}
                                >
                                    {applyToTenants ? (
                                        <svg
                                            width="11"
                                            height="11"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#fff"
                                            strokeWidth="3.2"
                                        >
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                    ) : null}
                                </span>
                                <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
                                    <span
                                        className="text-[var(--cpm-brand-navy)]"
                                        style={{ fontSize: 12.5, fontWeight: 800 }}
                                    >
                                        Also update this barcode on the affected store brands’ own
                                        records
                                    </span>
                                    <span
                                        className="text-[var(--cpm-text-secondary)]"
                                        style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
                                    >
                                        {isBarcodeConflicted
                                            ? 'Off: assigning updates the master barcode registry only. On: the winning product’s records gain it and every other claimant’s records lose it.'
                                            : 'Off: retiring updates the master barcode registry only. On: the barcode is removed from every claimant’s gtin list.'}
                                    </span>
                                </div>
                                <span
                                    className="ml-auto shrink-0 inline-flex items-center rounded-full uppercase"
                                    style={{
                                        padding: '3px 9px',
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                        background: applyToTenants
                                            ? 'var(--cpm-purple-tint-13)'
                                            : 'var(--cpm-surface-subtle)',
                                        border: `1px solid ${applyToTenants ? 'var(--cpm-purple-accent-border)' : 'var(--cpm-border-default)'}`,
                                        color: applyToTenants
                                            ? 'var(--cpm-brand-navy)'
                                            : 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    {applyToTenants ? 'ON' : 'OFF BY DEFAULT'}
                                </span>
                            </button>
                            {applyToTenants && dryRunPreview ? (
                                <div
                                    className="flex flex-col bg-[var(--cpm-purple-tint-5)] border border-[var(--cpm-purple-accent-border)]"
                                    style={{ gap: 11, padding: '12px 13px', borderRadius: 10 }}
                                >
                                    <span
                                        className="text-[var(--cpm-brand-navy)]"
                                        style={{ fontSize: 12.5, fontWeight: 700 }}
                                    >
                                        Dry-run preview: add {dryRunPreview.wouldAddCount}, remove{' '}
                                        {dryRunPreview.wouldRemoveCount}, affect{' '}
                                        {dryRunPreview.affectedTenantDocumentCount} tenant docs
                                    </span>
                                </div>
                            ) : null}
                        </div>
                        {renderVerdictRow(
                            barcodeVerdict,
                            !isBarcodeConflicted ? (
                                <>
                                    <CpmButton
                                        variant="navy"
                                        size="lg"
                                        onClick={() =>
                                            handleBarcode(CPM_BARCODE_RESOLUTION.ACKNOWLEDGE)
                                        }
                                        disabled={busy}
                                        isLoading={busy}
                                    >
                                        Keep trusting{' '}
                                        {claimantLabel(
                                            barcodeClaimants[0] ?? ({} as CpmBarcodeEntry)
                                        )}
                                    </CpmButton>
                                    <CpmButton
                                        variant="outline"
                                        size="lg"
                                        onClick={() =>
                                            handleBarcode(
                                                CPM_BARCODE_RESOLUTION.RETIRE,
                                                applyToTenants
                                            )
                                        }
                                        disabled={busy}
                                    >
                                        Retire this barcode
                                    </CpmButton>
                                    <CpmButton
                                        variant="ghost"
                                        size="lg"
                                        onClick={onClose}
                                        disabled={busy}
                                    >
                                        Leave it for a second look
                                    </CpmButton>
                                </>
                            ) : (
                                <>
                                    {winnerEntry ? (
                                        <CpmButton
                                            variant="navy"
                                            size="lg"
                                            onClick={() =>
                                                handleBarcode(
                                                    CPM_BARCODE_RESOLUTION.ASSIGN_TO,
                                                    applyToTenants
                                                )
                                            }
                                            disabled={busy || !winningVariantId}
                                            isLoading={busy}
                                        >
                                            Confirm — assign to {claimantLabel(winnerEntry)}
                                        </CpmButton>
                                    ) : null}
                                    {barcodeClaimants.map((c) => (
                                        <CpmButton
                                            key={c._id}
                                            variant={
                                                String(winningVariantId) ===
                                                String(c.productVariant)
                                                    ? 'navy'
                                                    : 'outline'
                                            }
                                            size="lg"
                                            onClick={() =>
                                                setWinningVariantId(String(c.productVariant))
                                            }
                                            disabled={busy}
                                        >
                                            Assign to {claimantLabel(c)}
                                        </CpmButton>
                                    ))}
                                    <CpmButton
                                        variant="ghost"
                                        size="lg"
                                        onClick={onClose}
                                        disabled={busy}
                                    >
                                        Leave as ambiguous for now
                                    </CpmButton>
                                </>
                            )
                        )}
                    </>
                ) : null}
            </CpmModalFooter>
        </CpmModal>
    );
}
